const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libCrypto = require('crypto');
const libAsync = require('async');

const DEFAULT_GUID_PREFIX = 'PCL';

// Per-entity cloning recipe.
//   `KeyFields`         — natural-key columns combined with the destination
//                         project identity to produce a deterministic upsert GUID.
//                         Also used to skip source rows whose natural key
//                         already exists in dest.
//   `CopyFields`        — pass-through values read straight from the source row.
//   `InheritFromDestProject`
//                       — columns stamped from the destination project record
//                         (IDCustomer, IDOrganization, IDProgram, …) so cloned
//                         rows live in the right tenant / org / program slice.
//                         In auto-create mode the "destination project record"
//                         is the source project (since the new dest project
//                         inherits customer / org / program from source).
//                         NOTE: `IDContract` is NOT in this list — contract
//                         resolution is per-call (see `RefContract`).
//   `RefContract`       — when true, the cloned record gets `IDContract` or
//                         `GUIDContract` stamped onto it from the per-call
//                         contract-ref, based on which contract-clone mode
//                         the run is in.
//   `GUIDFKs`           — cross-entity GUID-FK refs. Key is the field name to
//                         emit on the clone record (e.g. `GUIDBidItem`); value
//                         is `{ entity, sourceField }` describing which other
//                         cloned entity to look up and which source-row column
//                         supplies its source ID. The IntegrationAdapter resolves
//                         `GUID<Entity>` -> `ID<Entity>` at push time via the
//                         in-call HeadlightGUIDMap.
//   `DefaultMaxRecords` — soft cap on how many source rows to fetch. Heavy
//                         entities (Observation, Document) default to 5000; the
//                         caller can override per-entity via `pOptions.MaxRecords`.
//                         Unset = no cap. When the source-row count exceeds the
//                         cap, the slice emits a warning naming the entity, the
//                         total, and how many were skipped.
//   `OnlyEmitIfReferencedClonesPresent`
//                       — when true (e.g. DocumentObservationJoin), drop emitted
//                         rows whose source-FK targets aren't in the clone set
//                         (the referenced Document or Observation was capped out
//                         or excluded). Prevents orphan join rows.
//   `PostBuild`         — optional `(record, sourceRow, recipe) => void` hook
//                         called after a clone record is built but before it's
//                         bucketed. Lets recipes flip flags the API expects to
//                         see on the cloned body — e.g. `Labels.SAVE = true` so
//                         Observation's storeLabels hook materializes the tags.
//
// Order in this array IS the push order — entities must appear after anything
// they reference via GUIDFKs.
const CLONED_ENTITIES = [
	{
		Entity: 'BidItem',
		Prefix: 'PCL-BI',
		// Source IDBidItem is the most stable per-source-project identifier; using
		// it for the natural key means a re-clone targeting the same dest project
		// upserts in place rather than duplicating, while two source BidItems with
		// the same Name don't collapse into one dest row.
		KeyFields: ['IDBidItem'],
		CopyFields: ['ExternalSyncDate', 'Name', 'Description', 'Quantity', 'Units', 'CostPerUnit'],
		InheritFromDestProject: { IDCustomer: 'IDCustomer', IDOrganization: 'IDOrganization' },
		RefContract: true,
		GUIDFKs: {},
	},
	{
		Entity: 'LineItem',
		Prefix: 'PCL-LI',
		KeyFields: ['IDLineItem'],
		// Drop IDPayItem, IDFundingCategory, ParentIDLineItem — they reference
		// entities not in the clone surface, so copying source integer IDs would
		// leak cross-project refs. IDBidItem is bridged via GUIDFKs below.
		CopyFields: [
			'ExternalSyncDate', 'Name', 'Description', 'Notes', 'Units',
			'SpecificationYear', 'ItemCode', 'ItemNumber', 'CostPerUnit',
			'ExpectedQuantity', 'ContractorReportedQuantity', 'ActualQuantity',
			'ContractQuantity', 'InstalledQuantity', 'ChangeOrderQuantity',
			'ChangeOrderQuantityPending', 'Tax', 'ChangeOrderNumber',
			'FundingCategory', 'Status', 'ProposalIdentifier', 'PercentComplete',
			'CategoryNumber', 'Retainage', 'ImprovementType', 'DeliveryDate',
			'FHWAEligible',
		],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		RefContract: true,
		GUIDFKs:
		{
			GUIDBidItem: { entity: 'BidItem', sourceField: 'IDBidItem' },
		},
	},
	{
		Entity: 'ReportNamedInstanceProjectJoin',
		Prefix: 'PCL-RNIPJ',
		KeyFields: ['IDReportNamedInstance'],
		CopyFields: ['IDReportNamedInstance', 'Ordinal'],
		InheritFromDestProject: { IDProgram: 'IDProgram', IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		Entity: 'ObservationTagProjectJoin',
		Prefix: 'PCL-OTPJ',
		KeyFields: ['IDObservationTag'],
		CopyFields: ['IDObservationTag', 'Tag', 'TagType'],
		InheritFromDestProject: { IDOrganization: 'IDOrganization', IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		Entity: 'ProjectTagType',
		Prefix: 'PCL-PTT',
		KeyFields: ['TagType', 'Name'],
		CopyFields: ['TagType', 'Name', 'Ordinal'],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		Entity: 'ProjectDate',
		Prefix: 'PCL-PD',
		// Hash is the dedup key the platform already maintains for ProjectDate;
		// fall back to Name|Date when a source row has no Hash so we still get
		// a stable composite key per logical date.
		KeyFields: ['Hash', 'Name', 'Date'],
		CopyFields: ['Name', 'Date', 'Hash'],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		// Observation types enabled on the project. The manifest name is the
		// stable per-project identifier; the manifest content itself lives in
		// ObservationManifest and is referenced by name, so only the join is cloned.
		Entity: 'ProjectObservationManifestJoin',
		Prefix: 'PCL-POMJ',
		KeyFields: ['ObservationManifestName'],
		CopyFields: ['ObservationManifestName', 'Priority', 'Ordinal', 'Spreadsheet', 'Journal'],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		// Equipment on the project. Equipment is a customer master list, so only the
		// join is cloned; (IDProject, IDEquipment, IDOrganization) is unique, with
		// IDOrganization 0 for project-level equipment. Equipment labels are derived
		// from these rows by RebuildTags/Equipment, not cloned.
		Entity: 'ProjectEquipmentJoin',
		Prefix: 'PCL-PEJ',
		KeyFields: ['IDEquipment', 'IDOrganization'],
		CopyFields: ['IDEquipment', 'IDOrganization'],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
	},
	{
		// Observation rows themselves. Heavy entity — capped at 5000 by default.
		// `Labels` rides along verbatim; the slice sets `Labels.SAVE = true` via
		// the PostBuild hook so the API's storeLabels hook ([Observations.ts])
		// materializes the ObservationTagJoin rows on insert. Line-item label
		// semantics: Tag values are strings (e.g. ItemCode) which carry verbatim
		// because LineItems retain ItemCode across the clone.
		Entity: 'Observation',
		Prefix: 'PCL-OBS',
		KeyFields: ['IDObservation'],
		CopyFields: [
			'ExternalSyncDate', 'ExternalSyncGUID',
			'Name', 'Description',
			'ObservationType', 'Status', 'Priority', 'Version',
			'CaptureLatitude', 'CaptureLongitude', 'UserLatitude', 'UserLongitude',
			'IDDevice', 'AlignmentStation', 'AlignmentOffset',
			'AppHash', 'StateName', 'StateStep', 'Locked', 'Transcoded',
			// Labels is the auto-materialized array; carry it verbatim and let
			// the PostBuild hook flip SAVE=true so the server materializes the
			// ObservationTagJoin rows.
			'Labels',
		],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
		DefaultMaxRecords: 5000,
		PostBuild: (pRecord) =>
		{
			// The server's fetchLabels stamps Labels.SAVE = false on reads (so
			// round-tripping a record doesn't accidentally rewrite tags). For a
			// real clone we DO want them written, so flip the flag.
			if (pRecord.Labels && typeof(pRecord.Labels) === 'object')
			{
				pRecord.Labels.SAVE = true;
			}
		},
	},
	{
		// Document rows. Heavy entity — capped at 5000 by default. We deliberately
		// strip the auto-populated `Observations` integer array (CopyFields below
		// does not include it). If we let it carry, the server's updateObservations
		// hook would materialize DocumentObservationJoin rows from the SOURCE
		// IDObservation integers — a cross-project leak. The explicit
		// DocumentObservationJoin recipe below replaces that path.
		Entity: 'Document',
		Prefix: 'PCL-DOC',
		KeyFields: ['IDDocument'],
		CopyFields: [
			'ExternalSyncDate', 'ExternalSyncGUID',
			'Name', 'Description',
			'DocumentType', 'DocumentDate', 'Status', 'Version',
			'AppHash', 'Form',
			// FormData / EntityData ARE the document body — clone verbatim.
			// The schema columns are `FormData` and `EntityData` (objects);
			// the *JSON-suffix names only appear in legacy payloads. POSTing
			// either name lands in the non-suffix field; reads back via
			// getEntitySet expose only the non-suffix names, so the suffix
			// variants would never actually be copied across.
			'FormData', 'EntityData',
			'SequenceNumber', 'StateName', 'StateStep', 'Locked',
			'Timezone', 'Shift',
		],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs: {},
		DefaultMaxRecords: 5000,
	},
	{
		// Explicit document↔observation join. Pure GUID-FK chain — the
		// IntegrationAdapter resolves GUIDDocument / GUIDObservation to integer
		// IDs at push time via the in-call HeadlightGUIDMap. Composite natural
		// key keeps the deterministic GUID idempotent across re-clones.
		// `OnlyEmitIfReferencedClonesPresent` drops join rows whose Document or
		// Observation was capped out of the clone set, avoiding orphans.
		Entity: 'DocumentObservationJoin',
		Prefix: 'PCL-DOJ',
		KeyFields: ['IDDocument', 'IDObservation'],
		CopyFields: [],
		InheritFromDestProject: { IDCustomer: 'IDCustomer' },
		GUIDFKs:
		{
			GUIDDocument: { entity: 'Document', sourceField: 'IDDocument' },
			GUIDObservation: { entity: 'Observation', sourceField: 'IDObservation' },
		},
		// DocumentObservationJoin has no IDProject column, so we can't fetch
		// source rows with `FBV~IDProject~EQ~<id>` like every other recipe.
		// `SourceScope` tells _cloneEntity to look up the join rows by
		// `IDDocument INN (<source Document IDs cloned earlier>)` instead —
		// same pattern the sample-plan slice uses for MaterialLineItemJoin.
		// The ParentEntity must appear before this recipe in CLONED_ENTITIES
		// so its cloneIndex is populated by the time we get here.
		SourceScope: { ParentEntity: 'Document', FK: 'IDDocument' },
		OnlyEmitIfReferencedClonesPresent: true,
		// DOJ schema has no IDProject column — stamping pProjectRef would
		// make the DAL reject every insert with "Unknown column 'IDProject'".
		NoProjectRef: true,
	},
];

const SAMPLE_PLAN_ENTITIES = ['ProjectLineItemMaterialTest', 'MaterialLineItemJoin'];

// Fields cloned from the source Project row into a brand-new destination project.
// IDProjectType is intentionally on this list AND on UPDATE_EXISTING's partial
// update — the rule is "always apply source IDProjectType to the destination
// project, whether new or existing."
const PROJECT_CLONE_COPY_FIELDS = [
	'IDProjectType', 'IDCustomer', 'IDOrganization',
	'Timezone', 'Status', 'ImprovementTypeCode',
];

// Fields cloned from the source Contract row into either a new contract row
// (NEW_LINKED mode) or upserted into the destination project's existing
// contract (UPSERT_INTO_DEST mode). IDCustomer / IDOrganization scope the
// row to the right tenant.
const CONTRACT_CLONE_COPY_FIELDS = [
	'Status', 'DBEStructure',
	'ItemSpecificationYear', 'MaterialsSpecificationYear',
	'ItemIDTestSpecificationSet', 'MaterialIDTestSpecificationSet',
	'IDOrganization', 'IDCustomer', 'IDContractType',
];

class ProjectCloningAdapter extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'ProjectCloningAdapter';

		/**
		 * Concierge clients built by _buildConciergeClient, keyed by IDCustomer —
		 * one elevated-login + impersonation handshake per customer per adapter
		 * instance, retired together via retireConciergeClients.
		 * @type {Object<number, *>}
		 */
		this._ConciergeClients = {};
	}

	/** @returns {*} HeadlightRestClient registered on the fable by the caller. */
	get restClient()
	{
		return this.fable.HeadlightRestClient;
	}

	/**
	 * Deterministic GUID: `{prefix}-{md5(compositeKey).slice(0,12)}`.
	 * Same composite key → same GUID → idempotent upsert via the Integration Adapter.
	 *
	 * @param {string} pPrefix
	 * @param {string} pCompositeKey
	 * @returns {string}
	 */
	generateGUID(pPrefix, pCompositeKey)
	{
		const tmpHash = libCrypto.createHash('md5').update(pCompositeKey).digest('hex').substring(0, 12);
		return `${pPrefix}-${tmpHash}`;
	}

	/**
	 * Parse a value that is expected to be a JSON array (e.g. EmailRule.Projects,
	 * a TEXT column holding `[123,456]`). Tolerates an already-parsed array, and
	 * null / blank / malformed input by returning an empty array — mirroring the
	 * email service's own defensive reads of these fields.
	 *
	 * @param {*} pValue
	 * @returns {Array}
	 */
	_parseJSONArray(pValue)
	{
		if (Array.isArray(pValue)) return pValue;
		if (typeof(pValue) !== 'string' || pValue.trim().length === 0) return [];
		try
		{
			const tmpParsed = JSON.parse(pValue);
			return Array.isArray(tmpParsed) ? tmpParsed : [];
		}
		catch (pError)
		{
			return [];
		}
	}

	/**
	 * Batched INN filter to avoid URL length limits on large id lists.
	 *
	 * @param {string} pEntityType
	 * @param {string} pFilterField
	 * @param {Array} pIDs
	 * @param {string|null} pExtraFilter
	 * @param {function(Error|null, Array=)} fCallback
	 */
	getEntitySetBatched(pEntityType, pFilterField, pIDs, pExtraFilter, fCallback)
	{
		if (!pIDs || pIDs.length === 0)
		{
			return fCallback(null, []);
		}

		const tmpBatchSize = 50;
		const tmpBatches = [];
		for (let i = 0; i < pIDs.length; i += tmpBatchSize)
		{
			tmpBatches.push(pIDs.slice(i, i + tmpBatchSize));
		}

		let tmpAllRecords = [];
		let tmpBatchIndex = 0;
		const tmpProcessBatch = () =>
		{
			if (tmpBatchIndex >= tmpBatches.length)
			{
				return fCallback(null, tmpAllRecords);
			}
			// Do not include `Deleted~EQ~0` — the API backplane adds it automatically
			// for entities that have a Deleted column, and including it for entities
			// that don't (e.g. ReportNamedInstanceProjectJoin) causes the request to fail.
			let tmpFilter = `FBL~${pFilterField}~INN~${tmpBatches[tmpBatchIndex].join(',')}`;
			if (pExtraFilter)
			{
				tmpFilter += `~${pExtraFilter}`;
			}
			// Per-batch progress: each batch is a Count + N download requests round-tripped
			// through the rest client, so for large IDs lists this can take a while.
			// Without this line the caller sees nothing between the "Building …" header
			// and the final per-entity count.
			if (tmpBatches.length > 1)
			{
				this.log.info(`    ${pEntityType}: fetching batch ${tmpBatchIndex + 1}/${tmpBatches.length} (${tmpBatches[tmpBatchIndex].length} ${pFilterField} ids, ${tmpAllRecords.length} records so far)`);
			}
			this.restClient.getEntitySet(pEntityType, tmpFilter,
				(pError, pRecords) =>
				{
					if (pError)
					{
						return fCallback(pError);
					}
					tmpAllRecords = tmpAllRecords.concat(pRecords || []);
					tmpBatchIndex++;
					tmpProcessBatch();
				}, 0, 200000);
		};
		tmpProcessBatch();
	}

	/**
	 * Fetch rows of pEntity in pIDProject. Convenience wrapper around restClient.getEntitySet.
	 *
	 * @param {string} pEntity
	 * @param {number} pIDProject
	 * @param {number} pMaxRecords - record cap. Pass 0 / negative to use the default (200000).
	 * @param {function(Error|null, Array=)} fCallback
	 */
	getEntityRowsForProject(pEntity, pIDProject, pMaxRecords, fCallback)
	{
		const tmpLimit = (Number.isInteger(pMaxRecords) && pMaxRecords > 0) ? pMaxRecords : 200000;
		// `Deleted~EQ~0` is added by the API backplane for entities that have a
		// Deleted column; appending it here breaks entities that don't (e.g.
		// ReportNamedInstanceProjectJoin).
		const tmpFilter = `FBV~IDProject~EQ~${pIDProject}`;
		this.restClient.getEntitySet(pEntity, tmpFilter, fCallback, 0, tmpLimit);
	}

	/**
	 * Count rows of pEntity in pIDProject. Used to precheck whether a per-entity
	 * cap will fire, so we can surface a warning naming how many rows were skipped.
	 *
	 * @param {string} pEntity
	 * @param {number} pIDProject
	 * @param {function(Error|null, number=)} fCallback
	 */
	getEntityCountForProject(pEntity, pIDProject, fCallback)
	{
		// Defensive: not every HeadlightRestClient variant (or test stub) exposes
		// getEntitySetCount. If it's missing, fall back to "unknown" — the caller
		// can still proceed with a capped fetch and emit a "may have hit the cap"
		// warning, just without an exact skipped-row count.
		if (typeof(this.restClient.getEntitySetCount) !== 'function')
		{
			return fCallback(null, null);
		}
		const tmpFilter = `FBV~IDProject~EQ~${pIDProject}`;
		this.restClient.getEntitySetCount(pEntity, tmpFilter,
			(pError, pBody) =>
			{
				if (pError) return fCallback(pError);
				const tmpCount = (pBody && typeof(pBody.Count) === 'number') ? pBody.Count : 0;
				return fCallback(null, tmpCount);
			});
	}

	/**
	 * Lookup a Project by IDProject. Errors if not found.
	 *
	 * @param {number} pIDProject
	 * @param {function(Error|null, object=)} fCallback
	 */
	lookupProjectByID(pIDProject, fCallback)
	{
		this.restClient.getEntity('Project', pIDProject,
			(pError, pRecord) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				if (!pRecord || !pRecord.IDProject)
				{
					return fCallback(new Error(`Project [${pIDProject}] not found.`));
				}
				return fCallback(null, pRecord);
			});
	}

	/**
	 * Lookup a Contract by IDContract. Returns null (not error) when not found.
	 *
	 * @param {number} pIDContract
	 * @param {function(Error|null, object|null=)} fCallback
	 */
	lookupContractByID(pIDContract, fCallback)
	{
		if (!pIDContract || pIDContract <= 0)
		{
			return fCallback(null, null);
		}
		this.restClient.getEntity('Contract', pIDContract,
			(pError, pRecord) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				if (!pRecord || !pRecord.IDContract)
				{
					return fCallback(null, null);
				}
				return fCallback(null, pRecord);
			});
	}

	/**
	 * Build a comprehension that mirrors the source project's join / config rows
	 * (and sample-plan rows) onto a destination project. Does not write anything.
	 *
	 * Destination project modes:
	 *  - **UPDATE_EXISTING**: pDestIDProject > 0 — existing project; cloned rows
	 *    reference it by literal `IDProject`. A small partial-update record for
	 *    the dest Project itself is also pushed so its `IDProjectType` (and
	 *    sometimes `IDContract`) match the source.
	 *  - **AUTO_CREATE**:    pDestIDProject is null / 0 / undefined — a brand-new
	 *    Project row is added to the comprehension with a deterministic
	 *    `GUIDProject`; all cloned rows reference it via `GUIDProject`.
	 *
	 * Contract modes (controlled by `pOptions.CloneContract`):
	 *  - **NONE**:               source contract untouched; cloned rows get
	 *                            `IDContract: destProject.IDContract || 0`.
	 *  - **UPSERT_INTO_DEST**:   `CloneContract: true` AND dest project has
	 *                            an existing contract. Source contract field
	 *                            data is upserted INTO dest's existing contract
	 *                            row; cloned rows reference dest's existing
	 *                            `IDContract` literally.
	 *  - **NEW_LINKED**:         `CloneContract: true` AND dest project has
	 *                            no contract (or dest is being auto-created).
	 *                            A new Contract is created from the source
	 *                            contract; dest Project (new or existing) is
	 *                            updated to point at it via `GUIDContract`;
	 *                            cloned rows reference the new contract via
	 *                            `GUIDContract`.
	 *
	 * Always applies: source project's `IDProjectType` is stamped onto the
	 * destination project, whether new or existing.
	 *
	 * Also reads every Project-layer Configuration namespace set on the source
	 * (no cascade) and surfaces them on the returned envelope. Configuration
	 * blobs live in the separate Configuration microservice, not in Meadow, so
	 * they aren't part of the `comprehension` object — they're applied via a
	 * separate POST /Configuration/Put/… loop in `cloneProject` /
	 * `applyAllConfiguration`.
	 *
	 * @param {number} pSourceIDProject
	 * @param {number|null|undefined} pDestIDProject - omit / null / 0 to auto-create.
	 * @param {object} [pOptions]
	 * @param {boolean} [pOptions.CloneContract=false]
	 * @param {string[]} [pOptions.OnlyEntities] - When non-empty, only these entity
	 *        names are cloned (e.g. config-only seeding). The Project/Contract anchor
	 *        records are always emitted.
	 * @param {string[]} [pOptions.ExcludeEntities] - Entity names to skip (ignored
	 *        when OnlyEntities is provided).
	 * @param {string}  [pOptions.ProjectName]
	 * @param {string}  [pOptions.ProjectDescription]
	 * @param {string}  [pOptions.ContractName]
	 * @param {string}  [pOptions.ContractDescription]
	 * @param {function(Error|null, { comprehension: object, reportData: object, warnings: string[], configuration: object }=)} fCallback
	 */
	buildComprehension(pSourceIDProject, pDestIDProject, pOptions, fCallback)
	{
		// Back-compat: (src, dest, cb) — no options.
		if (typeof(pOptions) === 'function')
		{
			fCallback = pOptions;
			pOptions = {};
		}
		const tmpOptions = pOptions || {};
		const tmpCloneContract = !!tmpOptions.CloneContract;

		// Entity scoping — used by type-seeding to clone config-only (forms,
		// observation types, label types, labels, dates) while skipping
		// BidItem/LineItem/sample-plan. When OnlyEntities is a non-empty array,
		// only those entity names are emitted; otherwise any name in
		// ExcludeEntities is skipped. The Project (and Contract, when requested)
		// anchor records are always emitted regardless — for UPDATE_EXISTING the
		// Project record is a harmless partial update (IDProject + IDProjectType).
		const tmpOnlyEntities = (Array.isArray(tmpOptions.OnlyEntities) && tmpOptions.OnlyEntities.length > 0)
			? new Set(tmpOptions.OnlyEntities) : null;
		const tmpExcludeEntities = new Set(Array.isArray(tmpOptions.ExcludeEntities) ? tmpOptions.ExcludeEntities : []);
		const fIncludeEntity = (pEntity) => tmpOnlyEntities ? tmpOnlyEntities.has(pEntity) : !tmpExcludeEntities.has(pEntity);

		// Normalize dest: null / 0 / undefined / negative → auto-create.
		const tmpDestIDProject = (Number.isInteger(pDestIDProject) && pDestIDProject > 0) ? pDestIDProject : null;
		const tmpProjectMode = tmpDestIDProject ? 'UPDATE_EXISTING' : 'AUTO_CREATE';

		if (tmpProjectMode === 'UPDATE_EXISTING' && pSourceIDProject === tmpDestIDProject)
		{
			return fCallback(new Error('Source and destination IDProject must differ.'));
		}
		if (!Number.isInteger(pSourceIDProject) || pSourceIDProject <= 0)
		{
			return fCallback(new Error('SourceIDProject is required and must be a positive integer.'));
		}

		this.log.info(`Project Cloning Adapter — Build Comprehension`);
		this.log.info(`  Source IDProject:      ${pSourceIDProject}`);
		this.log.info(`  Destination IDProject: ${tmpDestIDProject || '(auto-create)'}`);
		this.log.info(`  Project mode:          ${tmpProjectMode}`);
		this.log.info(`  Clone contract:        ${tmpCloneContract}`);

		const tmpComprehension = {};
		const tmpCounts = {};
		const tmpWarnings = [];
		let tmpSourceProject = null;
		let tmpDestProject = null;
		let tmpSourceContract = null;
		let tmpConfiguration = {};   // { <NamespaceKey>: { <NamespaceValue>: blob } }
		// Per-rule EmailRule config to re-home after the rules are pushed (each
		// entry: { GUIDEmailRule, Handler, SourceConfig, SourceCustomerID, DestCustomerID }).
		let tmpEmailRuleConfigSync = [];
		const tmpCloneIndex = {};

		// Mode + refs computed in the project / contract slice stages and then
		// passed to entity cloning so per-row stamping is uniform.
		let tmpContractMode = 'NONE';
		let tmpProjectRef = null;     // { IDProject } or { GUIDProject }
		let tmpContractRef = null;    // { IDContract } or { GUIDContract }
		let tmpDestProjectScope = null;  // record used for InheritFromDestProject lookups

		libAsync.waterfall(
			[
				// Stage: source / dest project lookups.
				(fStageComplete) => this.lookupProjectByID(pSourceIDProject,
					(pError, pRecord) =>
					{
						if (pError) return fStageComplete(pError);
						tmpSourceProject = pRecord;
						return fStageComplete();
					}),
				(fStageComplete) =>
				{
					if (tmpProjectMode === 'AUTO_CREATE')
					{
						// No dest project to fetch; the "dest project scope" for
						// InheritFromDestProject is the source project (since the
						// new dest inherits customer / org / program from source).
						tmpDestProject = null;
						tmpDestProjectScope = tmpSourceProject;
						return fStageComplete();
					}
					this.lookupProjectByID(tmpDestIDProject,
						(pError, pRecord) =>
						{
							if (pError) return fStageComplete(pError);
							tmpDestProject = pRecord;
							tmpDestProjectScope = pRecord;
							return fStageComplete();
						});
				},
				// Stage: source contract lookup (only when CloneContract).
				(fStageComplete) =>
				{
					if (!tmpCloneContract)
					{
						return fStageComplete();
					}
					if (!tmpSourceProject.IDContract)
					{
						tmpWarnings.push(`CloneContract requested but source project [${pSourceIDProject}] has no contract — nothing to clone for the contract slice.`);
						return fStageComplete();
					}
					this.lookupContractByID(tmpSourceProject.IDContract,
						(pError, pRecord) =>
						{
							if (pError) return fStageComplete(pError);
							if (!pRecord)
							{
								tmpWarnings.push(`Source project's contract [IDContract=${tmpSourceProject.IDContract}] not found — contract slice will be skipped.`);
								return fStageComplete();
							}
							tmpSourceContract = pRecord;
							return fStageComplete();
						});
				},
				// Stage: determine contract mode + contract slice (if any).
				(fStageComplete) =>
				{
					// Determine contract mode based on options + dest project state.
					if (!tmpCloneContract || !tmpSourceContract)
					{
						tmpContractMode = 'NONE';
					}
					else if (tmpDestProject && tmpDestProject.IDContract && tmpDestProject.IDContract > 0)
					{
						tmpContractMode = 'UPSERT_INTO_DEST';
					}
					else
					{
						tmpContractMode = 'NEW_LINKED';
					}
					this.log.info(`  Contract mode:         ${tmpContractMode}`);

					const fBuildContractSlice = () =>
					{
						const tmpContractSlice = this._buildContractSlice(
							tmpSourceContract,
							tmpDestProject,
							tmpContractMode,
							tmpOptions);
						if (tmpContractSlice.record)
						{
							tmpComprehension.Contract = { [tmpContractSlice.recordGUIDKey]: tmpContractSlice.record };
							tmpCounts.Contract = tmpContractSlice.counts;
						}
						tmpContractRef = tmpContractSlice.contractRef;
						return fStageComplete();
					};

					// UPSERT_INTO_DEST upserts source contract field data INTO the dest's existing
					// contract row, keyed by that row's GUIDContract. tmpDestProject is a plain
					// Project record (lookupProjectByID) carrying only IDContract, not GUIDContract —
					// resolve the real contract GUID first, else the upsert can't match the existing
					// row and inserts a stray contract.
					if (tmpContractMode === 'UPSERT_INTO_DEST')
					{
						return this.lookupContractByID(tmpDestProject.IDContract,
							(pContractError, pDestContract) =>
							{
								if (pContractError) return fStageComplete(pContractError);
								if (pDestContract && pDestContract.GUIDContract)
								{
									tmpDestProject.GUIDContract = pDestContract.GUIDContract;
								}
								else
								{
									tmpWarnings.push(`UPSERT_INTO_DEST: could not resolve dest contract [IDContract=${tmpDestProject.IDContract}] GUIDContract; upsert may not match the existing row.`);
								}
								return fBuildContractSlice();
							});
					}
					return fBuildContractSlice();
				},
				// Stage: build the Project record (always — full clone for AUTO_CREATE,
				// partial update for UPDATE_EXISTING). This stage also locks in tmpProjectRef.
				(fStageComplete) =>
				{
					const tmpProjectSlice = this._buildProjectSlice(
						pSourceIDProject,
						tmpSourceProject,
						tmpDestIDProject,
						tmpDestProject,
						tmpProjectMode,
						tmpContractMode,
						tmpContractRef,
						tmpOptions);
					tmpComprehension.Project = { [tmpProjectSlice.recordGUIDKey]: tmpProjectSlice.record };
					tmpCounts.Project = tmpProjectSlice.counts;
					tmpProjectRef = tmpProjectSlice.projectRef;
					this.log.info(`  Project ref for cloned rows:  ${JSON.stringify(tmpProjectRef)}`);
					this.log.info(`  Contract ref for cloned rows: ${JSON.stringify(tmpContractRef)}`);
					return fStageComplete();
				},
				// Stage: clone all configured entities sequentially, in CLONED_ENTITIES order.
				(fStageComplete) =>
				{
					libAsync.eachSeries(CLONED_ENTITIES.filter((pRecipe) => fIncludeEntity(pRecipe.Entity)),
						(pRecipe, fEntityDone) =>
						{
							this._cloneEntity(pRecipe, pSourceIDProject, tmpDestIDProject, tmpDestProjectScope,
								tmpProjectRef, tmpContractRef, tmpCloneIndex, tmpOptions,
								(pError, pSlice) =>
								{
									if (pError) return fEntityDone(pError);
									tmpComprehension[pRecipe.Entity] = pSlice.bucket;
									tmpCounts[pRecipe.Entity] = pSlice.counts;
									for (const w of pSlice.warnings)
									{
										tmpWarnings.push(w);
									}
									return fEntityDone();
								});
						}, fStageComplete);
				},
				// Stage: sample-plan slice (PLIMT / MLJ). Skipped entirely when entity
				// scoping excludes both sample-plan entities (e.g. config-only seeding).
				(fStageComplete) =>
				{
					if (!SAMPLE_PLAN_ENTITIES.some((pEntity) => fIncludeEntity(pEntity)))
					{
						return fStageComplete();
					}
					this._buildSamplePlanSlice(pSourceIDProject, tmpProjectRef, tmpCloneIndex,
						(pError, pSlice) =>
						{
							if (pError) return fStageComplete(pError);
							for (const entity of SAMPLE_PLAN_ENTITIES)
							{
								if (pSlice.buckets[entity])
								{
									tmpComprehension[entity] = pSlice.buckets[entity];
									tmpCounts[entity] = pSlice.counts[entity];
								}
							}
							for (const w of pSlice.warnings) tmpWarnings.push(w);
							return fStageComplete();
						});
				},
				// Stage: email notification rules (EmailRule + EmailRuleSchedule).
				// Email rules aren't project-scoped by a column — membership is the
				// `Projects` JSON array — so this is a dedicated slice, not a
				// CLONED_ENTITIES recipe. The cloned rule's `Projects` array embeds
				// the NUMERIC dest IDProject, so we can only build it inline when the
				// dest id is known: UPDATE_EXISTING clone AND all type-seeding (the
				// target project already exists). For AUTO_CREATE the dest id isn't
				// known until the push lands, so we report candidate counts here (so
				// Preview shows a number) and defer the real build/push to cloneProject
				// once `_resolveDestIDProject` has the numeric id.
				(fStageComplete) =>
				{
					if (!fIncludeEntity('EmailRule'))
					{
						return fStageComplete();
					}
					const tmpSourceCustomerID = tmpSourceProject && tmpSourceProject.IDCustomer;
					if (tmpProjectMode === 'AUTO_CREATE')
					{
						this._fetchCloneableEmailRules(tmpSourceCustomerID, pSourceIDProject, null,
							(pError, pCloneableRules) =>
							{
								if (pError) return fStageComplete(pError);
								const tmpCandidateCount = (pCloneableRules || []).length;
								tmpCounts.EmailRule = { Source: tmpCandidateCount, ToCreate: tmpCandidateCount, Deferred: 'post-push' };
								return fStageComplete();
							});
						return;
					}
					const tmpDestCustomerID = tmpDestProjectScope && tmpDestProjectScope.IDCustomer;
					this._buildEmailRuleSlice(pSourceIDProject, tmpSourceCustomerID, tmpDestIDProject, tmpDestCustomerID, tmpOptions,
						(pError, pSlice) =>
						{
							if (pError) return fStageComplete(pError);
							tmpComprehension.EmailRule = pSlice.buckets.EmailRule;
							tmpComprehension.EmailRuleSchedule = pSlice.buckets.EmailRuleSchedule;
							tmpCounts.EmailRule = pSlice.counts.EmailRule;
							tmpCounts.EmailRuleSchedule = pSlice.counts.EmailRuleSchedule;
							tmpEmailRuleConfigSync = pSlice.configSync || [];
							for (const w of pSlice.warnings) tmpWarnings.push(w);
							return fStageComplete();
						});
				},
				// Stage: every Project-layer Configuration namespace (config service,
				// applied separately because it's owned by a sibling microservice with
				// its own write semantics and cache invalidation).
				(fStageComplete) =>
				{
					this._buildAllConfigurationSlices(pSourceIDProject,
						(pError, pSlice) =>
						{
							if (pError) return fStageComplete(pError);
							tmpConfiguration = pSlice.configByNamespaceKey;
							tmpCounts.Configuration = pSlice.counts;
							for (const w of pSlice.warnings) tmpWarnings.push(w);
							return fStageComplete();
						});
				},
			],
			(pError) =>
			{
				if (pError)
				{
					this.log.error(`Project clone comprehension build failed: ${(pError && pError.message) || pError}`, pError);
					return fCallback(pError);
				}
				const tmpResult = {
					comprehension: tmpComprehension,
					reportData:
					{
						SourceIDProject: pSourceIDProject,
						DestIDProject: tmpDestIDProject,
						ProjectMode: tmpProjectMode,
						ContractMode: tmpContractMode,
						// In AUTO_CREATE mode the dest project is identified by GUID until
						// the comprehension lands; surface it so post-apply callers (e.g.
						// applyAllConfiguration) can resolve to the new IDProject.
						DestProjectGUID: (tmpProjectRef && tmpProjectRef.GUIDProject) || null,
						Counts: tmpCounts,
					},
					warnings: tmpWarnings,
					configuration: tmpConfiguration,
					// Post-push work list to re-home each cloned rule's per-rule config.
					emailRuleConfigSync: tmpEmailRuleConfigSync,
				};
				return fCallback(null, tmpResult);
			});
	}

	/**
	 * Build one entity's slice of the comprehension.
	 *
	 * Idempotency comes from the deterministic per-row GUIDs (`<prefix>-<md5(destIdentifier|naturalKey)>`)
	 * combined with the IntegrationAdapter's upsert-by-GUID semantics. Re-cloning
	 * the same source into the same dest produces the same GUIDs, so the adapter
	 * UPDATEs existing rows in place rather than INSERTing duplicates. We do NOT
	 * pre-fetch dest rows to skip natural-key matches — that check was a no-op
	 * for AutoIdentity-keyed entities (BidItem/LineItem/Observation/Document)
	 * and the manual-pre-existing-row scenario it protected against on shared-FK
	 * entities is not part of the spec.
	 *
	 * @param {object} pRecipe - One CLONED_ENTITIES entry.
	 * @param {number} pSourceIDProject
	 * @param {number|null} pDestIDProject - null in AUTO_CREATE mode.
	 * @param {object} pDestProjectScope - Project record (dest in UPDATE_EXISTING,
	 *                                     source in AUTO_CREATE) used for
	 *                                     InheritFromDestProject field lookups.
	 * @param {object} pProjectRef - { IDProject } or { GUIDProject } stamped onto each clone record.
	 * @param {object} pContractRef - { IDContract } or { GUIDContract } stamped onto cloned
	 *                                rows whose recipe has `RefContract: true`.
	 * @param {object} pCloneIndex - Accumulator: { <entity>: { sourceIDToCloneGUID, sourceRows } }.
	 * @param {object} pOptions - buildComprehension options. Reads `pOptions.MaxRecords[<entity>]`
	 *                            to resolve the per-entity record cap (overrides recipe default).
	 * @param {function(Error|null, { bucket: object, counts: object, warnings: string[] }=)} fCallback
	 * @private
	 */
	_cloneEntity(pRecipe, pSourceIDProject, pDestIDProject, pDestProjectScope, pProjectRef, pContractRef, pCloneIndex, pOptions, fCallback)
	{
		const tmpOptions = pOptions || {};
		// Resolve the effective per-entity record cap. Option override > recipe default > no cap.
		const tmpMaxRecordsOption = (tmpOptions.MaxRecords && typeof(tmpOptions.MaxRecords) === 'object')
			? tmpOptions.MaxRecords[pRecipe.Entity]
			: undefined;
		let tmpEffectiveCap = null;
		if (Number.isInteger(tmpMaxRecordsOption) && tmpMaxRecordsOption > 0)
		{
			tmpEffectiveCap = tmpMaxRecordsOption;
		}
		else if (tmpMaxRecordsOption === 0)
		{
			tmpEffectiveCap = null; // explicit "no cap"
		}
		else if (Number.isInteger(pRecipe.DefaultMaxRecords) && pRecipe.DefaultMaxRecords > 0)
		{
			tmpEffectiveCap = pRecipe.DefaultMaxRecords;
		}

		this.log.info(`  Cloning ${pRecipe.Entity}${tmpEffectiveCap ? ` (cap: ${tmpEffectiveCap})` : ''}...`);
		const tmpCapWarnings = [];
		// Source-row fetcher. Two shapes:
		//   1. Default: filter by IDProject. Works for every recipe whose entity
		//      has an IDProject column. Honors per-entity caps with a precount
		//      so we can name how many rows are skipped in the warning.
		//   2. SourceScope: for entities like DocumentObservationJoin that have
		//      no IDProject column, fetch by INN against the source IDs of an
		//      already-cloned parent entity (e.g. IDDocument INN <doc src IDs>).
		//      The parent's cloneIndex is the bound here — no cap precount
		//      because the fetch is already scoped to a known finite set.
		const tmpFetchSource = (fDone) =>
		{
			if (pRecipe.SourceScope)
			{
				const tmpScope = pRecipe.SourceScope;
				const tmpParentIndex = pCloneIndex[tmpScope.ParentEntity];
				const tmpParentSourceIDs = (tmpParentIndex && tmpParentIndex.sourceIDToCloneGUID)
					? Object.keys(tmpParentIndex.sourceIDToCloneGUID).map((tmpID) => Number(tmpID)).filter((tmpID) => !!tmpID)
					: [];
				if (tmpParentSourceIDs.length === 0)
				{
					this.log.info(`    ${pRecipe.Entity}: no cloned ${tmpScope.ParentEntity} rows to scope against — skipping fetch.`);
					return fDone(null, []);
				}
				return this.getEntitySetBatched(pRecipe.Entity, tmpScope.FK, tmpParentSourceIDs, null, fDone);
			}
			const fGo = () => this.getEntityRowsForProject(pRecipe.Entity, pSourceIDProject, tmpEffectiveCap || 200000, fDone);
			if (!tmpEffectiveCap) return fGo();
			this.getEntityCountForProject(pRecipe.Entity, pSourceIDProject,
				(pCountError, pTotal) =>
				{
					if (pCountError)
					{
						this.log.info(`    ${pRecipe.Entity}: source-row precount failed (${pCountError.message || pCountError}) — proceeding to fetch capped at ${tmpEffectiveCap}.`);
						return fGo();
					}
					// pTotal === null means the rest client didn't expose a count
					// endpoint — proceed with the cap but without a skipped-row warning.
					if (pTotal !== null && pTotal > tmpEffectiveCap)
					{
						tmpCapWarnings.push(`${pRecipe.Entity}: source project has ${pTotal} row(s) but the per-entity cap is ${tmpEffectiveCap}; ${pTotal - tmpEffectiveCap} row(s) will be skipped.`);
					}
					return fGo();
				});
		};
		tmpFetchSource(
			(pError, pSourceRows) =>
			{
				if (pError) return fCallback(pError);

				const tmpSourceRows = pSourceRows || [];
				this.log.info(`    ${pRecipe.Entity}: ${tmpSourceRows.length} source row(s)`);

				// Stable per-run dest identifier used as the composite-key prefix
				// for deterministic GUID generation. Integer for UPDATE_EXISTING,
				// the new project's GUID (a string) for AUTO_CREATE — either way
				// a re-clone of the same (source, dest) produces the same GUIDs.
				// Idempotency: the IntegrationAdapter upserts by GUID, so re-cloning
				// the same source into the same dest updates rows in place rather
				// than duplicating. We intentionally do NOT pre-fetch dest rows
				// to "skip natural-key matches" — for AutoIdentity-keyed entities
				// (BidItem, LineItem, Observation, Document) the source's ID can't
				// match dest's, so the check is a no-op; for shared-FK / name-keyed
				// entities (RNI joins, ProjectTagType, ProjectDate) any pre-existing
				// dest row will simply coexist with the cloned row under a different
				// GUID — that's a manual-data scenario the spec doesn't ask us to
				// protect against.
				const tmpDestIdentifier = pDestIDProject
					? String(pDestIDProject)
					: (pProjectRef.GUIDProject || 'NEW');

				const tmpBucket = {};
				const tmpSourceIDToCloneGUID = {};
				const tmpSourceIDField = `ID${pRecipe.Entity}`;
				let tmpUnresolvedFKs = 0;
				let tmpDroppedOrphans = 0;
				const tmpGUIDFKs = pRecipe.GUIDFKs || {};
				const tmpGUIDFKFields = Object.keys(tmpGUIDFKs);
				const tmpInheritFromDestProject = pRecipe.InheritFromDestProject || {};
				const tmpWarnings = tmpCapWarnings.slice();

				for (const sourceRow of tmpSourceRows)
				{
					const tmpNaturalKey = this._naturalKey(pRecipe, sourceRow);
					const tmpGUIDKey = this.generateGUID(pRecipe.Prefix, `${tmpDestIdentifier}|${tmpNaturalKey}`);
					// Start with the per-call Project ref (IDProject literal or
					// GUIDProject FK). Recipes for entities that have no
					// IDProject column (e.g. DocumentObservationJoin) opt out
					// via `NoProjectRef: true` — otherwise the DAL rejects the
					// insert with "Unknown column 'IDProject' in 'field list'".
					const tmpRecord = pRecipe.NoProjectRef ? {} : Object.assign({}, pProjectRef);
					// Stamp GUID<Entity> onto the record body itself. The comprehension-processor's
					// runAdapter feeds the record (the bucket value) to IntegrationAdapter.addSourceRecord,
					// it does NOT pull the bucket key. Without this field on the body, the adapter
					// has no GUID to upsert by, the record fails to land, and downstream entities
					// that GUID-FK to it (e.g. PLIMT.GUIDLineItem) resolve to ID=0.
					tmpRecord[`GUID${pRecipe.Entity}`] = tmpGUIDKey;
					// Per-call Contract ref for entities that have IDContract.
					if (pRecipe.RefContract && pContractRef)
					{
						Object.assign(tmpRecord, pContractRef);
					}
					for (const field of pRecipe.CopyFields)
					{
						if (sourceRow[field] !== undefined && sourceRow[field] !== null)
						{
							tmpRecord[field] = sourceRow[field];
						}
					}
					for (const destField of Object.keys(tmpInheritFromDestProject))
					{
						const tmpSrcField = tmpInheritFromDestProject[destField];
						if (pDestProjectScope && pDestProjectScope[tmpSrcField] !== undefined && pDestProjectScope[tmpSrcField] !== null)
						{
							tmpRecord[destField] = pDestProjectScope[tmpSrcField];
						}
					}
					// Resolve cross-entity GUID-FK refs against the in-progress clone index.
					let tmpHasUnresolvedFK = false;
					for (const fkField of tmpGUIDFKFields)
					{
						const tmpFKDef = tmpGUIDFKs[fkField];
						const tmpSrcFKVal = sourceRow[tmpFKDef.sourceField];
						if (tmpSrcFKVal === undefined || tmpSrcFKVal === null || tmpSrcFKVal === 0)
						{
							continue;
						}
						const tmpRefIndex = pCloneIndex[tmpFKDef.entity];
						const tmpResolvedGUID = tmpRefIndex && tmpRefIndex.sourceIDToCloneGUID
							? tmpRefIndex.sourceIDToCloneGUID[tmpSrcFKVal]
							: undefined;
						if (tmpResolvedGUID)
						{
							tmpRecord[fkField] = tmpResolvedGUID;
						}
						else
						{
							tmpUnresolvedFKs++;
							tmpHasUnresolvedFK = true;
						}
					}
					// Recipes like DocumentObservationJoin opt out of emitting orphan
					// rows — if a side of the join lost its target (capped out or
					// excluded from the clone set), the join row has nothing to attach
					// to and is silently dropped.
					if (pRecipe.OnlyEmitIfReferencedClonesPresent && tmpHasUnresolvedFK)
					{
						tmpDroppedOrphans++;
						continue;
					}
					// PostBuild hook: lets the recipe flip flags on the cloned body
					// (e.g. Observation forces `Labels.SAVE = true` so the API's
					// storeLabels hook materializes the tags).
					if (typeof(pRecipe.PostBuild) === 'function')
					{
						try { pRecipe.PostBuild(tmpRecord, sourceRow, pRecipe); }
						catch (pHookError) { this.log.error(`PostBuild hook for ${pRecipe.Entity} threw: ${pHookError.message || pHookError}`); }
					}
					tmpBucket[tmpGUIDKey] = tmpRecord;
					const tmpSrcID = sourceRow[tmpSourceIDField];
					if (tmpSrcID !== undefined && tmpSrcID !== null)
					{
						tmpSourceIDToCloneGUID[tmpSrcID] = tmpGUIDKey;
					}
				}

				pCloneIndex[pRecipe.Entity] = {
					sourceIDToCloneGUID: tmpSourceIDToCloneGUID,
					sourceRows: tmpSourceRows,
				};

				if (tmpUnresolvedFKs > 0 && !pRecipe.OnlyEmitIfReferencedClonesPresent)
				{
					tmpWarnings.push(`${pRecipe.Entity}: ${tmpUnresolvedFKs} GUID-FK ref(s) could not be resolved (the referenced entity was either skipped or not in the clone set).`);
				}
				if (tmpDroppedOrphans > 0)
				{
					tmpWarnings.push(`${pRecipe.Entity}: dropped ${tmpDroppedOrphans} orphan row(s) whose source FK targets were not in the clone set.`);
				}

				return fCallback(null,
					{
						bucket: tmpBucket,
						counts:
						{
							Source: tmpSourceRows.length,
							ToCreate: Object.keys(tmpBucket).length,
						},
						warnings: tmpWarnings,
					});
			});
	}

	/**
	 * Stringify a row's natural-key fields into a stable lookup key.
	 * @private
	 */
	_naturalKey(pRecipe, pRow)
	{
		return pRecipe.KeyFields.map((f) => (pRow && pRow[f] !== undefined && pRow[f] !== null) ? String(pRow[f]) : '').join('|');
	}

	/**
	 * Build the Project record that lands in the comprehension AND determine the
	 * `projectRef` (`{ IDProject }` or `{ GUIDProject }`) that every cloned entity
	 * row will use to reference the destination project.
	 *
	 * AUTO_CREATE  → full clone from source + deterministic GUIDProject.
	 * UPDATE_EXISTING → partial update record carrying just IDProject, IDProjectType
	 *                   from source, and (if NEW_LINKED contract mode) GUIDContract.
	 *
	 * @returns {{ record: object, recordGUIDKey: string, projectRef: object, counts: object }}
	 * @private
	 */
	_buildProjectSlice(pSourceIDProject, pSourceProject, pDestIDProject, pDestProject,
		pProjectMode, pContractMode, pContractRef, pOptions)
	{
		const tmpOptions = pOptions || {};
		if (pProjectMode === 'AUTO_CREATE')
		{
			// Deterministic GUID — re-cloning the same source yields the same dest
			// project (idempotent upsert) rather than creating a new one each time.
			const tmpGUID = this.generateGUID('PCL-P', `FROM:${pSourceIDProject}`);
			const tmpRecord = { GUIDProject: tmpGUID };
			for (const field of PROJECT_CLONE_COPY_FIELDS)
			{
				if (pSourceProject[field] !== undefined && pSourceProject[field] !== null)
				{
					tmpRecord[field] = pSourceProject[field];
				}
			}
			// Caller overrides for the human-facing fields; fallback prefixes the source name.
			tmpRecord.Name = tmpOptions.ProjectName || `CLONED FROM ${pSourceProject.Name || `IDProject ${pSourceIDProject}`}`;
			if (tmpOptions.ProjectDescription !== undefined)
			{
				tmpRecord.Description = tmpOptions.ProjectDescription;
			}
			// Contract linkage on the new project.
			if (pContractRef && pContractRef.GUIDContract)
			{
				tmpRecord.GUIDContract = pContractRef.GUIDContract;
				delete tmpRecord.IDContract;
			}
			else if (pContractRef && pContractRef.IDContract !== undefined)
			{
				tmpRecord.IDContract = pContractRef.IDContract;
			}
			else
			{
				tmpRecord.IDContract = 0;
			}
			this.log.info(`  Project (AUTO_CREATE): GUIDProject=${tmpGUID} Name="${tmpRecord.Name}"`);
			return {
				record: tmpRecord,
				recordGUIDKey: tmpGUID,
				projectRef: { GUIDProject: tmpGUID },
				counts: { Mode: 'AUTO_CREATE', ToCreate: 1 },
			};
		}

		// UPDATE_EXISTING: minimal partial-update record. IDProjectType always from source
		// (per the always-apply rule). GUIDContract added only when we're creating a new
		// contract that the existing project needs to be linked to (NEW_LINKED).
		const tmpRecord = { IDProject: pDestIDProject };
		if (pSourceProject.IDProjectType !== undefined && pSourceProject.IDProjectType !== null)
		{
			tmpRecord.IDProjectType = pSourceProject.IDProjectType;
		}
		if (pContractMode === 'NEW_LINKED' && pContractRef && pContractRef.GUIDContract)
		{
			tmpRecord.GUIDContract = pContractRef.GUIDContract;
		}
		// Use the existing dest project's GUIDProject as the bucket key — the
		// adapter upserts by GUID against the existing row. If we don't have it
		// (shouldn't happen since lookupProjectByID returns it), fall back to a
		// generated key marked with the dest id for traceability.
		const tmpKey = pDestProject && pDestProject.GUIDProject
			? pDestProject.GUIDProject
			: this.generateGUID('PCL-PUPD', `DEST:${pDestIDProject}`);
		tmpRecord.GUIDProject = tmpKey;
		this.log.info(`  Project (UPDATE_EXISTING IDProject=${pDestIDProject}): IDProjectType=${tmpRecord.IDProjectType}${tmpRecord.GUIDContract ? ' GUIDContract=' + tmpRecord.GUIDContract : ''}`);
		return {
			record: tmpRecord,
			recordGUIDKey: tmpKey,
			projectRef: { IDProject: pDestIDProject },
			counts: { Mode: 'UPDATE_EXISTING', ToUpdate: 1 },
		};
	}

	/**
	 * Build the Contract record that lands in the comprehension AND determine the
	 * `contractRef` (`{ IDContract }` or `{ GUIDContract }`) that every contract-
	 * referencing cloned row will use.
	 *
	 * Returns `{ record: null, contractRef: { IDContract: ... } }` for NONE mode
	 * so the caller doesn't add a Contract entry to the comprehension.
	 *
	 * @returns {{ record: object|null, recordGUIDKey: string|null, contractRef: object, counts: object }}
	 * @private
	 */
	_buildContractSlice(pSourceContract, pDestProject, pContractMode, pOptions)
	{
		const tmpOptions = pOptions || {};
		if (pContractMode === 'NONE')
		{
			// Cloned rows reference dest project's existing contract literally (could be 0).
			const tmpIDContract = (pDestProject && pDestProject.IDContract) || 0;
			return {
				record: null,
				recordGUIDKey: null,
				contractRef: { IDContract: tmpIDContract },
				counts: { Mode: 'NONE' },
			};
		}

		if (pContractMode === 'UPSERT_INTO_DEST')
		{
			// Upsert source contract field data INTO dest's existing contract row.
			// Targeted by literal IDContract — no new row is created. Cloned entities
			// continue to reference dest's existing IDContract literally.
			const tmpDestIDContract = pDestProject.IDContract;
			const tmpRecord = { IDContract: tmpDestIDContract };
			for (const field of CONTRACT_CLONE_COPY_FIELDS)
			{
				if (pSourceContract[field] !== undefined && pSourceContract[field] !== null)
				{
					tmpRecord[field] = pSourceContract[field];
				}
			}
			if (tmpOptions.ContractName !== undefined)
			{
				tmpRecord.Name = tmpOptions.ContractName;
			}
			if (tmpOptions.ContractDescription !== undefined)
			{
				tmpRecord.Description = tmpOptions.ContractDescription;
			}
			// Bucket key must match the existing row's GUID so the adapter does an
			// UPDATE, not an INSERT.
			const tmpKey = pSourceContract && pDestProject && pDestProject.GUIDContract
				? pDestProject.GUIDContract
				: this.generateGUID('PCL-CUPD', `DEST:${tmpDestIDContract}`);
			tmpRecord.GUIDContract = tmpKey;
			this.log.info(`  Contract (UPSERT_INTO_DEST IDContract=${tmpDestIDContract}): upserting source contract fields in place`);
			return {
				record: tmpRecord,
				recordGUIDKey: tmpKey,
				contractRef: { IDContract: tmpDestIDContract },
				counts: { Mode: 'UPSERT_INTO_DEST', ToUpsert: 1 },
			};
		}

		// NEW_LINKED: brand-new contract row from source field data. Cloned entities
		// reference it via GUIDContract. The project record (new or existing-being-updated)
		// also references it via GUIDContract.
		const tmpGUID = this.generateGUID('PCL-C', `FROM:${pSourceContract.IDContract}`);
		const tmpRecord = { GUIDContract: tmpGUID };
		for (const field of CONTRACT_CLONE_COPY_FIELDS)
		{
			if (pSourceContract[field] !== undefined && pSourceContract[field] !== null)
			{
				tmpRecord[field] = pSourceContract[field];
			}
		}
		tmpRecord.Name = tmpOptions.ContractName || `CLONED FROM ${pSourceContract.Name || `IDContract ${pSourceContract.IDContract}`}`;
		if (tmpOptions.ContractDescription !== undefined)
		{
			tmpRecord.Description = tmpOptions.ContractDescription;
		}
		this.log.info(`  Contract (NEW_LINKED): GUIDContract=${tmpGUID} Name="${tmpRecord.Name}"`);
		return {
			record: tmpRecord,
			recordGUIDKey: tmpGUID,
			contractRef: { GUIDContract: tmpGUID },
			counts: { Mode: 'NEW_LINKED', ToCreate: 1 },
		};
	}

	/**
	 * Read every Project-layer Configuration namespace set on the source project.
	 *
	 * Two-step enumeration:
	 *  1. `GET /Describe/Configuration/In/Project/<src>` returns skeleton ConfigEntry
	 *     records — one per `(NamespaceKey, NamespaceValue)` pair set at the Project
	 *     layer for the source. We pull the distinct `NamespaceKey` set out of
	 *     that response.
	 *  2. For each `NamespaceKey`, `GET /Configuration/For/<key>/In/Project/<src>`
	 *     returns the reassembled blob keyed by `NamespaceValue`. The config
	 *     service handles the flatten→nested reconstruction; we just collect
	 *     the per-namespace blobs into a single map.
	 *
	 * The `/In/Project/<id>` form returns ONLY values set at the Project layer
	 * — no cascading. This preserves the destination's natural inheritance
	 * from Customer / Contract for everything the source didn't explicitly
	 * override.
	 *
	 * @param {number} pSourceIDProject
	 * @param {function(Error|null, { configByNamespaceKey: object, counts: object, warnings: string[] }=)} fCallback
	 * @private
	 */
	_buildAllConfigurationSlices(pSourceIDProject, fCallback)
	{
		this.log.info(`  Discovering Project-layer Configuration namespaces for IDProject ${pSourceIDProject}...`);
		const tmpDescribeURI = `Describe/Configuration/In/Project/${pSourceIDProject}`;
		this.restClient.getJSON(tmpDescribeURI,
			(pDescribeError, pDescribeBody) =>
			{
				if (pDescribeError)
				{
					this.log.error(`Failed to enumerate configuration namespaces for source project ${pSourceIDProject}: ${pDescribeError.message || pDescribeError}`);
					return fCallback(pDescribeError);
				}
				const tmpRows = Array.isArray(pDescribeBody) ? pDescribeBody : [];
				const tmpKeySet = new Set();
				for (const row of tmpRows)
				{
					if (row && row.NamespaceKey) tmpKeySet.add(row.NamespaceKey);
				}
				const tmpKeys = Array.from(tmpKeySet);
				this.log.info(`    Configuration: ${tmpKeys.length} NamespaceKey(s) — ${tmpKeys.join(', ') || '(none)'}`);

				if (tmpKeys.length === 0)
				{
					return fCallback(null,
						{
							configByNamespaceKey: {},
							counts: { Namespaces: 0, NamespaceValues: 0, ToCopy: 0 },
							warnings: [],
						});
				}

				const tmpConfigByNamespaceKey = {};
				const tmpWarnings = [];
				let tmpValuesCount = 0;
				libAsync.eachSeries(tmpKeys,
					(pKey, fNext) =>
					{
						const tmpFetchURI = `Configuration/For/${encodeURIComponent(pKey)}/In/Project/${pSourceIDProject}`;
						this.restClient.getJSON(tmpFetchURI,
							(pFetchError, pFetchBody) =>
							{
								if (pFetchError)
								{
									const errMsg = (pFetchError && pFetchError.message) || String(pFetchError);
									tmpWarnings.push(`Configuration/${pKey}: failed to read source blob (${errMsg}) — skipping.`);
									return fNext();
								}
								const tmpBlob = (pFetchBody && typeof(pFetchBody) === 'object') ? pFetchBody : {};
								const tmpValueKeys = Object.keys(tmpBlob);
								if (tmpValueKeys.length === 0)
								{
									return fNext();
								}
								tmpConfigByNamespaceKey[pKey] = tmpBlob;
								tmpValuesCount += tmpValueKeys.length;
								this.log.info(`    Configuration/${pKey}: ${tmpValueKeys.length} NamespaceValue(s)`);
								return fNext();
							});
					},
					() =>
					{
						return fCallback(null,
							{
								configByNamespaceKey: tmpConfigByNamespaceKey,
								counts: { Namespaces: Object.keys(tmpConfigByNamespaceKey).length, NamespaceValues: tmpValuesCount, ToCopy: tmpValuesCount },
								warnings: tmpWarnings,
							});
					});
			});
	}

	/**
	 * Build the sample-plan portion of the comprehension. Operates on the
	 * source LineItems already fetched by the LineItem clone step (read from
	 * `pCloneIndex.LineItem`). For each source LineItem that was cloned, emits
	 * a `ProjectLineItemMaterialTest` per source PLIMT and a deduped
	 * `MaterialLineItemJoin` per source MLJ, with `GUIDLineItem` pointing at
	 * the cloned (not-yet-created) destination LineItem. The IntegrationAdapter
	 * resolves `GUIDLineItem` -> `IDLineItem` at push time via HeadlightGUIDMap.
	 *
	 * GUID prefixes are kept as `MSP-PLIMT` / `MSP-MLJ` for upsert compatibility
	 * with rows previously seeded by the LIMS SamplePlan-Copy page.
	 *
	 * @param {number} pSourceIDProject
	 * @param {object} pProjectRef - { IDProject } or { GUIDProject } stamped onto each PLIMT record.
	 * @param {object} pCloneIndex - Accumulator populated by earlier _cloneEntity calls.
	 *                               Reads `pCloneIndex.LineItem.{sourceRows, sourceIDToCloneGUID}`.
	 * @param {function(Error|null, { buckets: object, counts: object, warnings: string[] }=)} fCallback
	 * @private
	 */
	_buildSamplePlanSlice(pSourceIDProject, pProjectRef, pCloneIndex, fCallback)
	{
		// Stable per-run dest identifier for composite-key prefixing.
		const tmpDestIdentifier = pProjectRef.IDProject
			? String(pProjectRef.IDProject)
			: (pProjectRef.GUIDProject || 'NEW');
		this.log.info(`  Building sample-plan slice (ProjectLineItemMaterialTest + MaterialLineItemJoin)...`);

		const tmpLineItemIndex = pCloneIndex.LineItem || { sourceRows: [], sourceIDToCloneGUID: {} };
		const tmpSourceLineItems = tmpLineItemIndex.sourceRows || [];
		const tmpSourceIDToCloneGUID = tmpLineItemIndex.sourceIDToCloneGUID || {};
		const tmpSourceLIIDs = tmpSourceLineItems.map((li) => li.IDLineItem);
		const tmpWarnings = [];

		if (tmpSourceLIIDs.length === 0)
		{
			this.log.info(`    No source LineItems were cloned — sample-plan slice will be empty.`);
			return fCallback(null,
				{
					buckets: { ProjectLineItemMaterialTest: {}, MaterialLineItemJoin: {} },
					counts:
					{
						ProjectLineItemMaterialTest: { Source: 0, ToCreate: 0 },
						MaterialLineItemJoin: { Source: 0, ToCreate: 0 },
					},
					warnings: tmpWarnings,
				});
		}

		let tmpSourcePLIMTs = [];
		let tmpSourceMLIJs = [];

		libAsync.waterfall(
			[
				(fStageComplete) =>
				{
					// PLIMT has its own IDProject column, so we can pull every row for
					// the source project in one filter — the rest client paginates the
					// response internally. Much faster than batching by IDLineItem INN,
					// which on a project with thousands of LineItems means dozens of
					// round-trips.
					// 0 → default cap (200000); PLIMT isn't user-capped today.
					this.getEntityRowsForProject('ProjectLineItemMaterialTest', pSourceIDProject, 0,
						(pError, pRows) =>
						{
							if (pError) return fStageComplete(pError);
							tmpSourcePLIMTs = pRows || [];
							this.log.info(`    ProjectLineItemMaterialTest (source IDProject ${pSourceIDProject}): ${tmpSourcePLIMTs.length} row(s)`);
							return fStageComplete();
						});
				},
				(fStageComplete) =>
				{
					// MaterialLineItemJoin has no IDProject column, so we have to fetch
					// it by IDLineItem INN against the source LineItem IDs.
					this.getEntitySetBatched('MaterialLineItemJoin', 'IDLineItem', tmpSourceLIIDs, null,
						(pError, pRows) =>
						{
							if (pError) return fStageComplete(pError);
							tmpSourceMLIJs = pRows || [];
							this.log.info(`    MaterialLineItemJoin (${tmpSourceLIIDs.length} source LineItems): ${tmpSourceMLIJs.length} row(s)`);
							return fStageComplete();
						});
				},
			],
			(pError) =>
			{
				if (pError) return fCallback(pError);

				const tmpPLIMTBucket = {};
				const tmpMLIJBucket = {};
				const tmpSeenMLIJ = {};
				let tmpOrphanedPLIMTs = 0;
				let tmpOrphanedMLIJs = 0;

				for (const src of tmpSourcePLIMTs)
				{
					const tmpCloneLIGUID = tmpSourceIDToCloneGUID[src.IDLineItem];
					if (!tmpCloneLIGUID)
					{
						// Source LineItem was skipped (e.g. natural-key collision) so its
						// material tests have nothing to attach to.
						tmpOrphanedPLIMTs++;
						continue;
					}
					const tmpCompositeKey = `${tmpDestIdentifier}|${src.IDLineItem}|${src.IDMaterial}|${src.IDTest}|${src.SampleType}`;
					const tmpGUID = this.generateGUID('MSP-PLIMT', tmpCompositeKey);
					tmpPLIMTBucket[tmpGUID] = Object.assign({}, pProjectRef, {
						GUIDProjectLineItemMaterialTest: tmpGUID,
						// GUID-FK — IntegrationAdapter resolves to the dest IDLineItem at push time.
						GUIDLineItem: tmpCloneLIGUID,
						IDMaterial: src.IDMaterial,
						IDTest: src.IDTest,
						IDLab: src.IDLab || 0,
						Intent: src.Intent || '',
						SampleType: src.SampleType || '',
						SampleSize: src.SampleSize || 0,
						SampleUnits: src.SampleUnits || '',
						Rate: src.Rate || 0,
						Frequency: src.Frequency || 0,
						Units: src.Units || '',
						TestDescription: src.TestDescription || '',
						SampleDescription: src.SampleDescription || '',
						SamplingInstructions: src.SamplingInstructions || '',
						SamplingRequirements: src.SamplingRequirements || '',
						SamplingPeriod: src.SamplingPeriod || '',
						Remarks: src.Remarks || '',
						ConversionFactor: src.ConversionFactor || 1,
						CertificateRequired: src.CertificateRequired || 0,
						TestsNotRequired: src.TestsNotRequired || 0,
						ErrorOmission: src.ErrorOmission || 0,
						ErrorOmissionDetail: src.ErrorOmissionDetail || '',
						ManualPlannedSampleCount: -1,
						ManualRequiredSampleCount: -1,
						ComputedPlannedSampleCount: 0,
						ComputedRequiredSampleCount: 0,
						CreatedSampleCount: 0,
						PassingSampleCount: 0,
						FailingSampleCount: 0,
						Deleted: 0,
					});
				}

				for (const src of tmpSourceMLIJs)
				{
					const tmpCloneLIGUID = tmpSourceIDToCloneGUID[src.IDLineItem];
					if (!tmpCloneLIGUID)
					{
						tmpOrphanedMLIJs++;
						continue;
					}
					// Dedup on (source IDLineItem, IDMaterial) — composite key matches the
					// MSP-MLJ key from when sample-plan-copy seeded dest by integer IDLineItem,
					// just substituted to the *source* LI's id for stability across re-clones.
					const tmpKey = `${src.IDLineItem}|${src.IDMaterial}`;
					if (tmpSeenMLIJ[tmpKey]) continue;
					tmpSeenMLIJ[tmpKey] = true;
					const tmpGUID = this.generateGUID('MSP-MLJ', `${tmpDestIdentifier}|${tmpKey}`);
					tmpMLIJBucket[tmpGUID] = {
						GUIDMaterialLineItemJoin: tmpGUID,
						GUIDLineItem: tmpCloneLIGUID,
						IDMaterial: src.IDMaterial,
						ConversionFactor: src.ConversionFactor || 1,
						ApprovedSourceRequired: src.ApprovedSourceRequired || 0,
						SpecialInstructions: src.SpecialInstructions || '',
						Units: src.Units || '',
						SampleRate: src.SampleRate || 0,
						SampleFrequency: src.SampleFrequency || 0,
						RequiredSampleCount: src.RequiredSampleCount || 0,
						Quantity: src.Quantity || 0,
						Deleted: 0,
					};
				}

				if (tmpOrphanedPLIMTs > 0)
				{
					tmpWarnings.push(`Sample plan: ${tmpOrphanedPLIMTs} source PLIMT row(s) skipped — their source LineItem was not in the clone set.`);
				}
				if (tmpOrphanedMLIJs > 0)
				{
					tmpWarnings.push(`Sample plan: ${tmpOrphanedMLIJs} source MLJ row(s) skipped — their source LineItem was not in the clone set.`);
				}

				return fCallback(null,
					{
						buckets:
						{
							ProjectLineItemMaterialTest: tmpPLIMTBucket,
							MaterialLineItemJoin: tmpMLIJBucket,
						},
						counts:
						{
							ProjectLineItemMaterialTest: { Source: tmpSourcePLIMTs.length, ToCreate: Object.keys(tmpPLIMTBucket).length },
							MaterialLineItemJoin: { Source: tmpSourceMLIJs.length, ToCreate: Object.keys(tmpMLIJBucket).length },
						},
						warnings: tmpWarnings,
					});
			});
	}

	/**
	 * Fetch the source customer's EmailRule rows that should be cloned onto the
	 * destination project. Email rules aren't project-scoped by a column — the
	 * `Projects` TEXT column holds a JSON array of project IDs (empty = "all
	 * projects" wildcard), so we read every rule for the customer and filter by
	 * membership in JS, exactly as the email service does when matching rules.
	 *
	 * A rule is cloneable when its `Projects` list is non-empty (a wildcard rule
	 * already applies to the destination — nothing to clone) AND includes the
	 * source project AND does not already include the destination project (that
	 * corresponding coverage effectively already exists).
	 *
	 * @param {number} pSourceCustomerID
	 * @param {number} pSourceIDProject
	 * @param {number|null} pDestIDProject - the numeric dest; null to skip the
	 *        "already includes dest" filter (used for AUTO_CREATE preview counts,
	 *        where the dest IDProject isn't known yet).
	 * @param {function(Error|null, Array=)} fCallback
	 * @private
	 */
	_fetchCloneableEmailRules(pSourceCustomerID, pSourceIDProject, pDestIDProject, fCallback)
	{
		if (!(Number(pSourceCustomerID) > 0))
		{
			return fCallback(null, []);
		}
		this.restClient.getEntitySet('EmailRule', `FBV~IDCustomer~EQ~${pSourceCustomerID}`,
			(pError, pRows) =>
			{
				if (pError) return fCallback(pError);
				const tmpCloneable = [];
				for (const tmpRule of (pRows || []))
				{
					const tmpProjects = this._parseJSONArray(tmpRule.Projects);
					// Wildcard (empty) rules already apply to the destination.
					if (tmpProjects.length === 0) continue;
					// Only rules that actually target the source project.
					if (!tmpProjects.includes(pSourceIDProject)) continue;
					// Skip when the destination is already covered by this rule.
					if (pDestIDProject && tmpProjects.includes(pDestIDProject)) continue;
					tmpCloneable.push(tmpRule);
				}
				return fCallback(null, tmpCloneable);
			}, 0, 200000);
	}

	/**
	 * Build the EmailRule + EmailRuleSchedule slice of the comprehension.
	 *
	 * For each cloneable source rule (see `_fetchCloneableEmailRules`) we emit a
	 * NEW corresponding rule that targets ONLY the destination project
	 * (`Projects: [destID]`), carrying a deterministic `PCL-ER-…` GUID so a
	 * re-clone upserts in place and the type-seeding prune (which keys off the
	 * `PCL-` prefix) can recognize it as seed-origin. The rule's Enabled state is
	 * preserved verbatim (a disabled source rule clones as disabled). `IDClonedRule`
	 * records the immediate source rule for audit.
	 *
	 * Scheduled rules (Handlers like ApprovalRollup / DailyTally) store their cron
	 * timing in a separate EmailRuleSchedule entity; a scheduled rule with no
	 * schedule row never fires, so each cloned rule's schedules ride along, bridged
	 * to the cloned rule via the `GUIDEmailRule` GUID-FK (the comprehension
	 * processor resolves it to the new IDEmailRule at push time).
	 *
	 * Requires a NUMERIC destination IDProject (the `Projects` array embeds it as a
	 * literal — the processor can't resolve a GUID inside a JSON string). Callers
	 * therefore only invoke this once the dest IDProject is known: inline for
	 * UPDATE_EXISTING clone + all type-seeding, and post-resolution for AUTO_CREATE.
	 *
	 * @param {number} pSourceIDProject
	 * @param {number} pSourceCustomerID - customer whose rules we read.
	 * @param {number} pDestIDProject - the resolved numeric destination project.
	 * @param {number} pDestCustomerID - customer stamped onto the cloned rows.
	 * @param {object} pOptions
	 * @param {function(Error|null, { buckets: object, counts: object, warnings: string[] }=)} fCallback
	 * @private
	 */
	_buildEmailRuleSlice(pSourceIDProject, pSourceCustomerID, pDestIDProject, pDestCustomerID, pOptions, fCallback)
	{
		this.log.info(`  Building email-rule slice (EmailRule + EmailRuleSchedule)...`);
		const tmpWarnings = [];
		const tmpEmailRuleBucket = {};
		const tmpEmailRuleScheduleBucket = {};
		// Map a source rule's IDEmailRule to the cloned rule's GUID so schedules can
		// bridge to their (re)parented rule.
		const tmpSourceRuleIDToCloneGUID = {};
		// Per-rule config to re-home after push (skips the shared 'default').
		const tmpConfigSync = [];

		this._fetchCloneableEmailRules(pSourceCustomerID, pSourceIDProject, pDestIDProject,
			(pError, pCloneableRules) =>
			{
				if (pError) return fCallback(pError);

				for (const tmpRule of pCloneableRules)
				{
					const tmpGUID = this.generateGUID('PCL-ER', `${pDestIDProject}|${tmpRule.IDEmailRule}`);
					tmpSourceRuleIDToCloneGUID[tmpRule.IDEmailRule] = tmpGUID;
					tmpEmailRuleBucket[tmpGUID] = {
						GUIDEmailRule: tmpGUID,
						IDCustomer: pDestCustomerID,
						// The corresponding rule targets ONLY the destination project.
						// `Projects` is a JSON-array column the API serializes on write,
						// so emit a raw array — pre-stringifying double-encodes it to
						// a `"[id]"` string that no longer parses as a project list.
						Projects: [pDestIDProject],
						DocumentType: tmpRule.DocumentType || '',
						// `Status` is likewise a JSON-array column; normalize to a raw
						// array (the read may hand back an array or a JSON string).
						Status: this._parseJSONArray(tmpRule.Status),
						Handler: tmpRule.Handler || '',
						Config: tmpRule.Config || '',
						// Preserve the source rule's enabled/disabled state.
						Enabled: tmpRule.Enabled || 0,
						// Audit pointer back to the immediate source rule.
						IDClonedRule: tmpRule.IDEmailRule,
					};

					// A per-rule config (NamespaceValue keyed by the source rule's own
					// id — the app writes `IDEmailRule-<id>`) must be copied to the
					// cloned rule's own namespace value post-push; the shared 'default'
					// (or an empty Config) already exists at the dest customer.
					const tmpRuleConfig = (typeof(tmpRule.Config) === 'string') ? tmpRule.Config.trim() : '';
					if (tmpRuleConfig !== '' && tmpRuleConfig !== 'default')
					{
						tmpConfigSync.push(
							{
								GUIDEmailRule: tmpGUID,
								Handler: tmpRule.Handler || '',
								SourceConfig: tmpRuleConfig,
								SourceCustomerID: pSourceCustomerID,
								DestCustomerID: pDestCustomerID,
							});
					}
				}

				// `Config` names a CUSTOMER-level configuration namespace; when the
				// clone crosses customers that namespace may not exist under the dest
				// customer. Surface it rather than silently shipping a dangling ref.
				if (pCloneableRules.length > 0 && Number(pSourceCustomerID) !== Number(pDestCustomerID))
				{
					tmpWarnings.push(`Email rules: cloned ${pCloneableRules.length} rule(s) across customers (${pSourceCustomerID} → ${pDestCustomerID}); each rule's Config namespace may not exist under the destination customer and may need to be provisioned.`);
				}

				const tmpSourceRuleIDs = pCloneableRules.map((pRule) => pRule.IDEmailRule);
				if (tmpSourceRuleIDs.length === 0)
				{
					return fCallback(null, this._emailRuleSliceResult(tmpEmailRuleBucket, tmpEmailRuleScheduleBucket, pCloneableRules.length, 0, tmpWarnings, tmpConfigSync));
				}

				// Fetch the schedules for exactly the rules we're cloning. ERS has no
				// IDProject column, so fetch by IDEmailRule INN against the source rule ids.
				this.getEntitySetBatched('EmailRuleSchedule', 'IDEmailRule', tmpSourceRuleIDs, null,
					(pScheduleError, pScheduleRows) =>
					{
						if (pScheduleError) return fCallback(pScheduleError);
						let tmpOrphanedSchedules = 0;
						for (const tmpSchedule of (pScheduleRows || []))
						{
							const tmpCloneRuleGUID = tmpSourceRuleIDToCloneGUID[tmpSchedule.IDEmailRule];
							if (!tmpCloneRuleGUID)
							{
								// Parent rule wasn't cloned — nothing to attach to.
								tmpOrphanedSchedules++;
								continue;
							}
							const tmpScheduleGUID = this.generateGUID('PCL-ERS', `${pDestIDProject}|${tmpSchedule.IDEmailRuleSchedule}`);
							tmpEmailRuleScheduleBucket[tmpScheduleGUID] = {
								GUIDEmailRuleSchedule: tmpScheduleGUID,
								// GUID-FK — resolved to the cloned rule's IDEmailRule at push time.
								GUIDEmailRule: tmpCloneRuleGUID,
								IDCustomer: pDestCustomerID,
								DayOfMonth: (tmpSchedule.DayOfMonth != null) ? tmpSchedule.DayOfMonth : -1,
								DayOfWeek: (tmpSchedule.DayOfWeek != null) ? tmpSchedule.DayOfWeek : -1,
								HourOfDay: tmpSchedule.HourOfDay || 0,
							};
						}
						if (tmpOrphanedSchedules > 0)
						{
							tmpWarnings.push(`Email rules: ${tmpOrphanedSchedules} schedule row(s) skipped — their parent rule was not in the clone set.`);
						}
						return fCallback(null, this._emailRuleSliceResult(
							tmpEmailRuleBucket, tmpEmailRuleScheduleBucket, pCloneableRules.length,
							(pScheduleRows || []).length - tmpOrphanedSchedules, tmpWarnings, tmpConfigSync));
					});
			});
	}

	/**
	 * Shape the email-rule slice return envelope (buckets + counts + warnings),
	 * matching the sample-plan slice's `{ Source, ToCreate }` count convention.
	 * @private
	 */
	_emailRuleSliceResult(pEmailRuleBucket, pEmailRuleScheduleBucket, pRuleSourceCount, pScheduleSourceCount, pWarnings, pConfigSync)
	{
		return {
			buckets:
			{
				EmailRule: pEmailRuleBucket,
				EmailRuleSchedule: pEmailRuleScheduleBucket,
			},
			counts:
			{
				EmailRule: { Source: pRuleSourceCount, ToCreate: Object.keys(pEmailRuleBucket).length },
				EmailRuleSchedule: { Source: pScheduleSourceCount, ToCreate: Object.keys(pEmailRuleScheduleBucket).length },
			},
			warnings: pWarnings,
			configSync: pConfigSync || [],
		};
	}

	/**
	 * Push a previously-built comprehension to the server by delegating to
	 * `headlight-comprehension-processor`. The processor scans each record for
	 * `GUID<Entity>` / `_GUID<Entity>` / `_Dest_IDEntity_*_Via_GUID<Entity>`
	 * fields, builds a dependency graph, topologically sorts entities, then
	 * pushes each through a fresh `headlight-integration-adapter` with the
	 * same options `/PushComprehension`'s `applyComprehensionSubtree` uses
	 * (`SimpleMarshal: true`, `ForceMarshal: true`, empty prefixes — so our
	 * `PCL-LI-…` / `MSP-PLIMT-…` keys are used as-is and cross-entity FKs
	 * resolve via the in-call `HeadlightGUIDMap`).
	 *
	 * The `require` is intentionally lazy: this keeps consumers that only
	 * call `buildComprehension` (e.g. the API service's `GET /ProjectClone/Preview`
	 * bundle, which doesn't vendor the processor) from failing on module load.
	 *
	 * Why not POST to /PushComprehension instead? Two reasons that don't go
	 * away with the processor refactor:
	 *  - CLI consumers authenticate as a real user; /PushComprehension is gated
	 *    for trusted internal services, not user-facing tools.
	 *  - Server-side consumers (a hypothetical `POST /ProjectClone/Apply`)
	 *    would be making a loopback HTTP hop on the same service when the
	 *    processor is callable in-process.
	 *
	 * @param {object} pComprehension - Output of buildComprehension().comprehension.
	 * @param {function(Error|null)} fCallback
	 */
	loadComprehension(pComprehension, fCallback)
	{
		const libComprehensionProcessor = require('headlight-comprehension-processor');
		if (!this.fable.servicesMap.ComprehensionProcessor)
		{
			this.fable.addServiceType('ComprehensionProcessor', libComprehensionProcessor);
		}
		const tmpProcessor = this.fable.instantiateServiceProviderWithoutRegistration('ComprehensionProcessor');
		// Hand it our already-authenticated rest client so it skips its own
		// CheckSession / authenticate round-trip and pushes through the same
		// session the caller set up.
		tmpProcessor.client = this.restClient;
		return tmpProcessor.integrateComprehension(pComprehension, fCallback);
	}

	/**
	 * Apply every Project-layer Configuration namespace to a destination project
	 * via the Configuration microservice. Iterates each `(NamespaceKey,
	 * NamespaceValue)` pair and POSTs to `Configuration/Put/<NamespaceKey>/
	 * <NamespaceValue>/IDProject/<dest>`. Order doesn't matter — each pair is
	 * independent. POSTs that succeed invalidate the config service's cache
	 * as a side-effect of going through `/Configuration/Put`, so dest reads
	 * pick up the new values without manual cache busting.
	 *
	 * Per-pair errors are collected into the result rather than aborting the
	 * loop, so one bad blob doesn't orphan the rest.
	 *
	 * @param {number} pDestIDProject
	 * @param {object} pConfigByNamespaceKey - Output of buildComprehension().configuration:
	 *        `{ <NamespaceKey>: { <NamespaceValue>: <blob> }, … }`.
	 * @param {function(Error|null, { applied: number, errors: Array<{ NamespaceKey: string, NamespaceValue: string, Error: string }> }=)} fCallback
	 */
	applyAllConfiguration(pDestIDProject, pConfigByNamespaceKey, fCallback)
	{
		const tmpConfig = pConfigByNamespaceKey || {};
		// Flatten into a list of POST jobs we can iterate with libAsync.eachSeries.
		const tmpJobs = [];
		for (const tmpKey of Object.keys(tmpConfig))
		{
			const tmpBlob = tmpConfig[tmpKey] || {};
			for (const tmpValue of Object.keys(tmpBlob))
			{
				tmpJobs.push({ NamespaceKey: tmpKey, NamespaceValue: tmpValue, Body: tmpBlob[tmpValue] });
			}
		}
		if (tmpJobs.length === 0)
		{
			this.log.info('[ProjectCloning] No Configuration to apply',
				{ Action: 'ProjectCloning-ConfigEmpty', IDProject: pDestIDProject });
			return fCallback(null, { applied: 0, errors: [] });
		}

		this.log.info('[ProjectCloning] Applying Configuration namespaces',
			{ Action: 'ProjectCloning-ConfigApplyStart', IDProject: pDestIDProject, Jobs: tmpJobs.length, Namespaces: Object.keys(tmpConfig).length });

		// Configuration writes require elevated privileges. Spin up a parallel
		// rest client impersonated as the dest customer's Concierge user (see
		// _buildConciergeClient) so the requesting user's own client — which
		// typically lacks Configuration-write rights — never touches the
		// Configuration service. The caller's client is left untouched either way.
		this.restClient.getEntity('Project', pDestIDProject,
			(pProjError, pDestProject) =>
			{
				if (pProjError || !pDestProject || !pDestProject.IDCustomer)
				{
					this.log.warn('[ProjectCloning] Could not resolve IDCustomer for Concierge lookup; falling back to caller client',
						{ Action: 'ProjectCloning-ConfigFallbackNoCustomer', IDProject: pDestIDProject, Error: pProjError ? (pProjError.message || String(pProjError)) : null });
					return this._writeConfigurationPOSTs(this.restClient, pDestIDProject, tmpJobs, fCallback);
				}
				this._buildConciergeClient(pDestProject.IDCustomer,
					(pErr, pConciergeClient) =>
					{
						// pErr is non-fatal — _buildConciergeClient logs and
						// returns null when concierge resolution or impersonation
						// fails. Either way we end up with a usable client.
						const tmpClient = pConciergeClient || this.restClient;
						if (!pConciergeClient)
						{
							this.log.warn('[ProjectCloning] Falling back to caller client for Configuration writes',
								{ Action: 'ProjectCloning-ConfigFallback', IDProject: pDestIDProject, IDCustomer: pDestProject.IDCustomer });
						}
						return this._writeConfigurationPOSTs(tmpClient, pDestIDProject, tmpJobs, fCallback);
					});
			});
	}

	/**
	 * Find the Concierge machine-account user for a customer. Concierge users
	 * follow the convention of a GUIDUser ending in `-Concierge` (the customer
	 * provisioning bootstrap creates them with that suffix), so we filter by
	 * IDCustomer + a LIKE on GUIDUser. There's at most one per customer; if
	 * none is found the caller falls back to the requesting user's own client.
	 *
	 * @param {number} pIDCustomer
	 * @param {function(Error|null, number)} fCallback - IDUser, or 0 when no concierge exists.
	 */
	_resolveConciergeUserID(pIDCustomer, fCallback)
	{
		const tmpFilter = `FBV~IDCustomer~EQ~${pIDCustomer}~FBV~GUIDUser~LK~%25-Concierge`;
		this.restClient.getEntitySet('User', tmpFilter,
			(pError, pUsers) =>
			{
				if (pError) return fCallback(pError, 0);
				const tmpUser = (Array.isArray(pUsers) && pUsers.length > 0) ? pUsers[0] : null;
				return fCallback(null, (tmpUser && tmpUser.IDUser) ? Number(tmpUser.IDUser) : 0);
			},
			0, 1);
	}

	/**
	 * Build a separate HeadlightRestClient instance impersonated as the
	 * customer's Concierge user. The flow mirrors the
	 * `HeadlightEssentialDataProvider.getImpersonatedSession` pattern used in
	 * the materials micro-service:
	 *
	 *   1. Spawn a fresh HeadlightRestClient (so the caller's client is
	 *      untouched).
	 *   2. Authenticate it as the configured `HeadLight.ElevatedUser` — a
	 *      service-account user whose privileges include impersonation. The
	 *      requesting user typically does NOT have impersonation rights, so
	 *      we never use the caller's session for the handshake.
	 *   3. `GET /Authentication/Impersonate/<conciergeIDUser>` to swap that
	 *      same client's session onto the concierge.
	 *   4. Hand the resulting client back to the caller — every subsequent
	 *      request through it runs as the concierge.
	 *
	 * No `X-Service-Trust` header is set: concierge privileges are expected
	 * to be sufficient for Configuration writes on their own.
	 *
	 * Returns `null` (not an error) when concierge resolution, ElevatedUser
	 * auth, or impersonation fail — the caller falls back to its own client
	 * and the Configuration POSTs will at least surface whatever permission
	 * error is in play, instead of getting silently lost.
	 *
	 * @param {number} pIDCustomer
	 * @param {function(Error|null, *|null)} fCallback - impersonated client or null.
	 */
	/**
	 * Log out every cached concierge session and clear the cache. Callers that
	 * drive multiple elevated steps (email-rule config sync + Configuration
	 * writes) call this once at the end of their run; the concierge session is
	 * a live privileged credential and must not linger until TTL. Best-effort:
	 * a failed logout is logged and never surfaces as an error.
	 *
	 * @param {function(Error|null)} fCallback
	 */
	retireConciergeClients(fCallback)
	{
		const tmpClients = Object.values(this._ConciergeClients);
		this._ConciergeClients = {};
		if (tmpClients.length === 0)
		{
			return fCallback(null);
		}
		libAsync.eachSeries(tmpClients,
			(pClient, fDone) =>
			{
				const tmpSession = this._clientSession(pClient);
				if (!tmpSession || !tmpSession.SessionID || typeof pClient.deauthenticate !== 'function')
				{
					return fDone();
				}
				pClient.deauthenticate(() => fDone());
			},
			() => fCallback(null));
	}

	/**
	 * The client's live session, across client variants (the real
	 * HeadlightRestClient exposes `.session`; stubs may only expose
	 * `getSessionData()`).
	 *
	 * @param {*} pClient
	 * @returns {object|null}
	 */
	_clientSession(pClient)
	{
		if (!pClient)
		{
			return null;
		}
		if (pClient.session)
		{
			return pClient.session;
		}
		return (typeof pClient.getSessionData === 'function') ? pClient.getSessionData() : null;
	}

	_buildConciergeClient(pIDCustomer, fCallback)
	{
		const tmpCached = this._ConciergeClients[pIDCustomer];
		const tmpCachedSession = this._clientSession(tmpCached);
		if (tmpCachedSession && tmpCachedSession.SessionID)
		{
			return fCallback(null, tmpCached);
		}
		// ElevatedUser creds come from the same settings path the materials
		// service reads — `HeadLight.ElevatedUser.{Username,Password}` — so
		// deployments configure one shared service account, not a per-app one.
		const tmpHeadLightSettings = (this.fable.settings && this.fable.settings.HeadLight) || {};
		const tmpElevatedUser = tmpHeadLightSettings.ElevatedUser || {};
		if (!tmpElevatedUser.Username || !tmpElevatedUser.Password)
		{
			this.log.warn('[ProjectCloning] HeadLight.ElevatedUser not configured; cannot build concierge client',
				{ Action: 'ProjectCloning-ConciergeSkipUnconfigured', IDCustomer: pIDCustomer });
			return fCallback(null, null);
		}

		this._resolveConciergeUserID(pIDCustomer,
			(pError, pConciergeIDUser) =>
			{
				if (pError)
				{
					this.log.warn('[ProjectCloning] Concierge lookup failed',
						{ Action: 'ProjectCloning-ConciergeLookupError', IDCustomer: pIDCustomer, Error: pError.message || String(pError) });
					return fCallback(null, null);
				}
				if (!pConciergeIDUser)
				{
					this.log.warn('[ProjectCloning] No Concierge user found for customer',
						{ Action: 'ProjectCloning-ConciergeMissing', IDCustomer: pIDCustomer });
					return fCallback(null, null);
				}

				let tmpConciergeClient;
				try
				{
					// CRITICAL: pass ServerURL + EnforceSSL from the caller's
					// (already-configured) client. Otherwise the parallel
					// HeadlightRestClient falls back to its module-level default —
					// which is the hardcoded QA URL
					// (`https://api.qa.headlight.com/1.0/`, see
					// `defaultHeadLightRestClientOptions` in
					// `headlight-rest-client`). On PROD that means the ElevatedUser
					// login attempt goes to QA, where the same username has a
					// different password, so the auth silently fails and the
					// caller falls back to its own unprivileged client for
					// Configuration writes.
					const clientOptions =
					{
						ServerURL: this.restClient && this.restClient.serverURL,
						EnforceSSL: this.restClient && this.restClient.enforceSSL,
					};
					this.log.info(`[ProjectCloning] instantiating dedicated client for concierge`,
						{ Action: 'ProjectCloning-ConciergeClientSetup', IDCustomer: pIDCustomer, clientOptions });
					tmpConciergeClient = this.fable.instantiateServiceProviderWithoutRegistration('HeadlightRestClient', clientOptions);
				}
				catch (pInstErr)
				{
					this.log.warn('[ProjectCloning] Could not instantiate parallel HeadlightRestClient',
						{ Action: 'ProjectCloning-ConciergeClientInstantiationError', IDCustomer: pIDCustomer, Error: pInstErr.message || String(pInstErr) });
					return fCallback(null, null);
				}
				if (!tmpConciergeClient)
				{
					this.log.warn('[ProjectCloning] Parallel HeadlightRestClient instantiation returned nothing',
						{ Action: 'ProjectCloning-ConciergeClientNull', IDCustomer: pIDCustomer });
					return fCallback(null, null);
				}

				if (typeof tmpConciergeClient.authenticateWithCredentials !== 'function'
					|| typeof tmpConciergeClient.impersonate !== 'function')
				{
					this.log.warn('[ProjectCloning] HeadlightRestClient missing authenticateWithCredentials/impersonate',
						{ Action: 'ProjectCloning-ConciergeClientMissingMethods', IDCustomer: pIDCustomer });
					return fCallback(null, null);
				}

				// Step 1: log in as the ElevatedUser. This client now holds the
				// elevated session — used ONLY long enough to impersonate the
				// concierge, never reused for tenant data writes.
				tmpConciergeClient.authenticateWithCredentials(tmpElevatedUser.Username, tmpElevatedUser.Password,
					(pAuthError) =>
					{
						if (pAuthError)
						{
							this.log.error('[ProjectCloning] ElevatedUser authentication failed',
								{
									Action: 'ProjectCloning-ElevatedUserAuthFailed',
									IDCustomer: pIDCustomer,
									ElevatedUsername: tmpElevatedUser.Username,
									Error: pAuthError.message || String(pAuthError),
								});
							return fCallback(null, null);
						}
						// Step 2: swap the elevated session onto the concierge.
						tmpConciergeClient.impersonate(pConciergeIDUser,
							(pImpError) =>
							{
								if (pImpError)
								{
									this.log.error('[ProjectCloning] Concierge impersonation failed',
										{
											Action: 'ProjectCloning-ConciergeImpersonateFailed',
											IDCustomer: pIDCustomer,
											ConciergeIDUser: pConciergeIDUser,
											Error: pImpError.message || String(pImpError),
										});
									return fCallback(null, null);
								}
								this.log.info('[ProjectCloning] Concierge impersonation active',
									{ Action: 'ProjectCloning-ConciergeImpersonateSuccess', IDCustomer: pIDCustomer, ConciergeIDUser: pConciergeIDUser });
								this._ConciergeClients[pIDCustomer] = tmpConciergeClient;
								return fCallback(null, tmpConciergeClient);
							});
					});
			});
	}

	/**
	 * Iterate the Configuration POST jobs through the supplied client. Pulled
	 * out of `applyAllConfiguration` so the same loop runs whether we end up
	 * with the concierge-impersonated client or the caller's own.
	 *
	 * @param {*} pClient
	 * @param {number} pDestIDProject
	 * @param {Array<{NamespaceKey:string, NamespaceValue:string, Body:*}>} pJobs
	 * @param {function(Error|null, { applied:number, errors:Array }=)} fCallback
	 */
	/**
	 * Deep field-level merge: plain objects recurse; on any other conflict
	 * (scalar, array, type mismatch) the overlay wins; base-only keys are
	 * preserved. Neither input is mutated.
	 *
	 * @param {object} pBase
	 * @param {object} pOverlay
	 * @returns {object}
	 */
	_deepMergeObjects(pBase, pOverlay)
	{
		const tmpMerged = {};
		for (const tmpKey in (pBase || {}))
		{
			tmpMerged[tmpKey] = pBase[tmpKey];
		}
		for (const tmpKey in (pOverlay || {}))
		{
			const tmpBaseValue = tmpMerged[tmpKey];
			const tmpOverlayValue = pOverlay[tmpKey];
			const tmpBothObjects = tmpBaseValue && typeof tmpBaseValue === 'object' && !Array.isArray(tmpBaseValue)
				&& tmpOverlayValue && typeof tmpOverlayValue === 'object' && !Array.isArray(tmpOverlayValue);
			tmpMerged[tmpKey] = tmpBothObjects ? this._deepMergeObjects(tmpBaseValue, tmpOverlayValue) : tmpOverlayValue;
		}
		return tmpMerged;
	}

	/**
	 * Merge each job's source blob over the destination's CURRENT Project-layer
	 * value for the same (NamespaceKey, NamespaceValue) — source wins conflicts,
	 * dest-only fields survive. One read per distinct NamespaceKey
	 * (`Configuration/For/<key>/In/Project/<dest>`, Project layer only, no
	 * cascade). A failed read logs a warning and leaves that job's blob
	 * unmerged rather than blocking the write.
	 *
	 * @param {*} pClient
	 * @param {number} pDestIDProject
	 * @param {Array<{ NamespaceKey: string, NamespaceValue: string, Body: object }>} pJobs
	 * @param {function(Array<object>)} fCallback - The jobs with merged bodies.
	 */
	_mergeConfigurationJobs(pClient, pDestIDProject, pJobs, fCallback)
	{
		const tmpKeys = Array.from(new Set(pJobs.map((pJob) => pJob.NamespaceKey)));
		const tmpDestBlobsByKey = {};
		libAsync.eachSeries(tmpKeys,
			(pKey, fNext) =>
			{
				const tmpFetchURI = `Configuration/For/${encodeURIComponent(pKey)}/In/Project/${pDestIDProject}`;
				pClient.getJSON(tmpFetchURI,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.log.warn('[ProjectCloning] Destination Configuration read failed; writing this namespace unmerged',
								{ Action: 'ProjectCloning-ConfigMergeReadWarning', IDProject: pDestIDProject, NamespaceKey: pKey, Error: pError.message || String(pError) });
						}
						else if (pBody && typeof pBody === 'object')
						{
							tmpDestBlobsByKey[pKey] = pBody;
						}
						return fNext();
					});
			},
			() =>
			{
				const tmpMergedJobs = pJobs.map((pJob) =>
				{
					const tmpDestValue = tmpDestBlobsByKey[pJob.NamespaceKey] && tmpDestBlobsByKey[pJob.NamespaceKey][pJob.NamespaceValue];
					const tmpBothObjects = tmpDestValue && typeof tmpDestValue === 'object' && !Array.isArray(tmpDestValue)
						&& pJob.Body && typeof pJob.Body === 'object' && !Array.isArray(pJob.Body);
					if (!tmpBothObjects)
					{
						return pJob;
					}
					return { NamespaceKey: pJob.NamespaceKey, NamespaceValue: pJob.NamespaceValue, Body: this._deepMergeObjects(tmpDestValue, pJob.Body) };
				});
				return fCallback(tmpMergedJobs);
			});
	}

	_writeConfigurationPOSTs(pClient, pDestIDProject, pJobs, fCallback)
	{
		this.log.info('[ProjectCloning] Writing Configuration namespace-value pairs',
			{ Action: 'ProjectCloning-ConfigWriteStart', IDProject: pDestIDProject, Jobs: pJobs.length });
		const tmpErrors = [];
		let tmpApplied = 0;

		this._mergeConfigurationJobs(pClient, pDestIDProject, pJobs, (pMergedJobs) =>
		{
			libAsync.eachSeries(pMergedJobs,
				(pJob, fNext) =>
				{
					// URL form matters: Put/:NS/:NV/:LevelContext/:LevelContextID
					// writes at the named LevelContext, but the alternate
					// Put/:NS/:NV/IDProject/:IDProject form does NOT — the handler
					// falls back to LevelContext='User' because the route doesn't
					// capture :LevelContext. We want Project-layer writes so the
					// matching read path (Describe + For/<NS>/In/Project) sees them,
					// so we use the explicit /Project/<id> form here.
					// See api/config/source/endpoints/Configuration-Put-ByNamespace.js
					// line 184: `const levelContext = request.params.LevelContext || 'User';`
					const tmpURI = `Configuration/Put/${encodeURIComponent(pJob.NamespaceKey)}/${encodeURIComponent(pJob.NamespaceValue)}/Project/${pDestIDProject}`;
					pClient.postJSON(tmpURI, pJob.Body,
						(pError) =>
						{
							if (pError)
							{
								const errMsg = (pError && pError.message) || String(pError);
								this.log.error('[ProjectCloning] Configuration write failed',
									{
										Action: 'ProjectCloning-ConfigWriteError',
										IDProject: pDestIDProject,
										NamespaceKey: pJob.NamespaceKey,
										NamespaceValue: pJob.NamespaceValue,
										Error: errMsg,
									});
								tmpErrors.push({ NamespaceKey: pJob.NamespaceKey, NamespaceValue: pJob.NamespaceValue, Error: errMsg });
							}
							else
							{
								tmpApplied++;
								this.log.info('[ProjectCloning] Configuration write applied',
									{
										Action: 'ProjectCloning-ConfigWriteApplied',
										IDProject: pDestIDProject,
										NamespaceKey: pJob.NamespaceKey,
										NamespaceValue: pJob.NamespaceValue,
									});
							}
							return fNext();
						});
				},
				() =>
				{
					this.log.info('[ProjectCloning] Configuration writes complete',
						{
							Action: 'ProjectCloning-ConfigWriteComplete',
							IDProject: pDestIDProject,
							Jobs: pJobs.length,
							Applied: tmpApplied,
							Errors: tmpErrors.length,
						});
					return fCallback(null, { applied: tmpApplied, errors: tmpErrors });
				});
		});
	}

	/**
	 * Build + apply in one call. Convenience for CLI / one-shot server callers.
	 * Pushes the Meadow comprehension first, then writes the full Configuration
	 * blob (every Project-layer namespace) via the Configuration service. The two
	 * are independent; configuration comes second because it's typically small
	 * and we want the heavy work to surface errors first.
	 *
	 * In AUTO_CREATE mode the dest project doesn't have an IDProject until after
	 * the comprehension lands. After loadComprehension we look the new project up
	 * by its deterministic GUIDProject to get its IDProject for the configuration step.
	 *
	 * @param {number} pSourceIDProject
	 * @param {number|null|undefined} pDestIDProject - omit / null / 0 to auto-create.
	 * @param {object} [pOptions] - see buildComprehension options.
	 * @param {function(Error|null, { build: object, configuration: object, resolvedDestIDProject: number }=)} fCallback
	 */
	cloneProject(pSourceIDProject, pDestIDProject, pOptions, fCallback)
	{
		if (typeof(pOptions) === 'function')
		{
			fCallback = pOptions;
			pOptions = {};
		}
		this.buildComprehension(pSourceIDProject, pDestIDProject, pOptions,
			(pError, pBuild) =>
			{
				if (pError) return fCallback(pError);
				this.loadComprehension(pBuild.comprehension,
					(pLoadError) =>
					{
						if (pLoadError) return fCallback(pLoadError, { build: pBuild });
						// Resolve the dest IDProject — known up-front in UPDATE_EXISTING,
						// looked up by GUID in AUTO_CREATE.
						this._resolveDestIDProject(pBuild,
							(pResolveError, pResolvedID) =>
							{
								if (pResolveError) return fCallback(pResolveError, { build: pBuild });
								// AUTO_CREATE deferred the email-rule slice (the dest IDProject
								// wasn't known at build time). Now that it's resolved, build and
								// push that slice before applying Configuration.
								this._applyDeferredEmailRules(pBuild, pResolvedID, pOptions,
									(pEmailError) =>
									{
										if (pEmailError) return fCallback(pEmailError, { build: pBuild, resolvedDestIDProject: pResolvedID });
										// Re-home each cloned rule's per-rule config now that the rules
										// have new ids (post-push). Warnings only — never fails the clone.
										this.syncClonedEmailRuleConfigs(pBuild.emailRuleConfigSync,
											(pSyncErr, pSyncResult) =>
											{
												if (pSyncResult && Array.isArray(pSyncResult.warnings))
												{
													for (const w of pSyncResult.warnings) pBuild.warnings.push(w);
												}
											this.applyAllConfiguration(pResolvedID, pBuild.configuration,
												(pConfigError, pConfigResult) =>
												{
													return fCallback(pConfigError,
														{
															build: pBuild,
															configuration: pConfigResult,
															resolvedDestIDProject: pResolvedID,
														});
												});
										});
											});
							});
					});
			});
	}

	/**
	 * Resolve the destination IDProject after a comprehension push. In
	 * UPDATE_EXISTING mode it's already in the build result. In AUTO_CREATE mode
	 * we look it up by the deterministic GUIDProject we generated at build time.
	 * @private
	 */
	_resolveDestIDProject(pBuild, fCallback)
	{
		const tmpDestID = pBuild && pBuild.reportData && pBuild.reportData.DestIDProject;
		if (tmpDestID)
		{
			return fCallback(null, tmpDestID);
		}
		const tmpGUID = pBuild && pBuild.reportData && pBuild.reportData.DestProjectGUID;
		if (!tmpGUID)
		{
			return fCallback(new Error('Could not resolve destination IDProject: neither DestIDProject nor DestProjectGUID is set on the build result.'));
		}
		this.restClient.getEntitySet('Project', `FBV~GUIDProject~EQ~${tmpGUID}`,
			(pError, pRows) =>
			{
				if (pError) return fCallback(pError);
				if (!pRows || pRows.length === 0)
				{
					return fCallback(new Error(`Auto-created Project [GUIDProject=${tmpGUID}] was not found on the server after push.`));
				}
				return fCallback(null, pRows[0].IDProject);
			}, 0, 1);
	}

	/**
	 * Build and push the email-rule slice that `buildComprehension` deferred in
	 * AUTO_CREATE mode (where the dest IDProject — which the cloned rules' `Projects`
	 * array must embed — wasn't known until the main push resolved the new project).
	 * A no-op for UPDATE_EXISTING (the slice was already built inline and pushed with
	 * the rest of the comprehension) and when EmailRule was excluded. On success it
	 * overwrites the deferred candidate counts on `pBuild.reportData.Counts` with the
	 * real ones.
	 *
	 * @param {object} pBuild - the buildComprehension result.
	 * @param {number} pResolvedDestIDProject
	 * @param {object} pOptions
	 * @param {function(Error|null)} fCallback
	 * @private
	 */
	_applyDeferredEmailRules(pBuild, pResolvedDestIDProject, pOptions, fCallback)
	{
		const tmpDeferred = pBuild && pBuild.reportData && pBuild.reportData.Counts && pBuild.reportData.Counts.EmailRule;
		// Only the AUTO_CREATE preview-count path stamps `Deferred: 'post-push'`.
		if (!tmpDeferred || tmpDeferred.Deferred !== 'post-push')
		{
			return fCallback(null);
		}
		// In AUTO_CREATE the new dest inherits its customer from the source, so the
		// source project's IDCustomer is both the rule-read customer and the customer
		// stamped onto the cloned rows.
		this.lookupProjectByID(pBuild.reportData.SourceIDProject,
			(pError, pSourceProject) =>
			{
				if (pError) return fCallback(pError);
				const tmpCustomerID = pSourceProject && pSourceProject.IDCustomer;
				this._buildEmailRuleSlice(pBuild.reportData.SourceIDProject, tmpCustomerID, pResolvedDestIDProject, tmpCustomerID, pOptions || {},
					(pSliceError, pSlice) =>
					{
						if (pSliceError) return fCallback(pSliceError);
						pBuild.reportData.Counts.EmailRule = pSlice.counts.EmailRule;
						pBuild.reportData.Counts.EmailRuleSchedule = pSlice.counts.EmailRuleSchedule;
						pBuild.emailRuleConfigSync = pSlice.configSync || [];
						for (const w of pSlice.warnings) pBuild.warnings.push(w);
						this.loadComprehension(
							{ EmailRule: pSlice.buckets.EmailRule, EmailRuleSchedule: pSlice.buckets.EmailRuleSchedule },
							(pLoadError) => fCallback(pLoadError));
					});
			});
	}

	/**
	 * Re-home each cloned rule's per-rule Configuration after the rules are
	 * pushed. An EmailRule's config lives in a CUSTOMER-scoped Configuration
	 * entry keyed by `NamespaceKey: EmailRule-<Handler>` / `NamespaceValue:
	 * <rule.Config>`, and the app writes that value as `IDEmailRule-<the rule's
	 * own id>`. Carrying the source value verbatim would (a) share the source
	 * rule's config on a same-customer clone and (b) dangle on a cross-customer
	 * clone. So for each cloned rule we resolve its new id, copy the source blob
	 * to `IDEmailRule-<newId>` at the dest customer, and point the rule's Config
	 * at it. Per-rule failures warn and continue — they never fail the clone.
	 *
	 * Public post-push entry point: `cloneProject` calls it after `loadComprehension`,
	 * and the type-seeding service calls it after applying its own diff-delete
	 * comprehension. Both hand in the `emailRuleConfigSync` list `buildComprehension`
	 * produced. A `null`/empty list is a no-op.
	 *
	 * @param {Array<{GUIDEmailRule:string, Handler:string, SourceConfig:string, SourceCustomerID:number, DestCustomerID:number}>} pMeta
	 * @param {function(Error|null, { synced:number, warnings:string[] }=)} fCallback
	 */
	syncClonedEmailRuleConfigs(pMeta, fCallback)
	{
		const tmpMeta = Array.isArray(pMeta) ? pMeta : [];
		if (tmpMeta.length === 0)
		{
			return fCallback(null, { synced: 0, warnings: [] });
		}
		this.log.info(`  Re-homing ${tmpMeta.length} cloned email-rule config(s)...`);
		const tmpSourceCustomerID = tmpMeta[0].SourceCustomerID;
		const tmpDestCustomerID = tmpMeta[0].DestCustomerID;
		// Configuration writes need elevated privileges the requesting user
		// typically lacks — even a customer admin — which is the same reason
		// `applyAllConfiguration` writes through a Concierge. So the config write MUST
		// run through the Concierge (impersonated as the dest customer). We route the
		// rule resolve/read/update through it too: it's already built, keeps this to a
		// single client, and also stays correct in the less-common case where the
		// acting session's customer isn't the rule's (e.g. a super-user- or
		// platform-driven clone). Same-customer cloning is the norm; if the Concierge
		// can't be built we fall back to the caller client (fine for reads/updates in
		// that norm, but the Configuration write will likely be rejected).
		this._buildConciergeClient(tmpDestCustomerID,
			(pDestErr, pDestConcierge) =>
			{
				const tmpDestClient = pDestConcierge || this.restClient;
				if (!pDestConcierge)
				{
					this.log.warn('[ProjectCloning] Falling back to caller client for dest email-rule config writes/updates (will likely be rejected)',
						{ Action: 'ProjectCloning-EmailRuleConfigFallback', IDCustomer: tmpDestCustomerID });
				}
				const fRun = (pSrcClient) => this._runEmailRuleConfigSync(tmpMeta, pSrcClient, tmpDestClient, fCallback);
				// Same-customer clone (the common case): one Concierge serves both.
				if (Number(tmpSourceCustomerID) === Number(tmpDestCustomerID))
				{
					return fRun(tmpDestClient);
				}
				// Cross-customer: the source blob lives under the source customer,
				// so read it with a source-customer Concierge.
				this._buildConciergeClient(tmpSourceCustomerID,
					(pSrcErr, pSrcConcierge) =>
					{
						if (!pSrcConcierge)
						{
							this.log.warn('[ProjectCloning] Falling back to caller client for source email-rule config reads',
								{ Action: 'ProjectCloning-EmailRuleConfigFallback', IDCustomer: tmpSourceCustomerID });
						}
						return fRun(pSrcConcierge || this.restClient);
					});
			});
	}

	/**
	 * Per-rule config re-home loop. `pSrcClient` reads the source-customer config;
	 * `pDestClient` resolves the cloned rule, writes the dest-customer config, and
	 * updates the rule — both elevated (see `syncClonedEmailRuleConfigs`).
	 * @private
	 */
	_runEmailRuleConfigSync(pMeta, pSrcClient, pDestClient, fCallback)
	{
		const tmpWarnings = [];
		let tmpSynced = 0;
		libAsync.eachSeries(pMeta,
			(pEntry, fNext) =>
			{
				// 1. Resolve the cloned rule's new id by its deterministic GUID
				//    (dest-customer scoped — must use the elevated dest client).
				pDestClient.getEntitySet('EmailRule', `FBV~GUIDEmailRule~EQ~${pEntry.GUIDEmailRule}`,
					(pFindErr, pRows) =>
					{
						const tmpRule = (!pFindErr && Array.isArray(pRows)) ? pRows[0] : null;
						if (!tmpRule || !tmpRule.IDEmailRule)
						{
							tmpWarnings.push(`Email rule config: cloned rule ${pEntry.GUIDEmailRule} not found post-push — its config was not cloned.`);
							return fNext();
						}
						const tmpNewID = tmpRule.IDEmailRule;
						// 2. Read the source rule's config blob (source-customer scoped).
						const tmpReadURI = `Configuration/For/EmailRule-${encodeURIComponent(pEntry.Handler)}/In/Customer/${pEntry.SourceCustomerID}`;
						pSrcClient.getJSON(tmpReadURI,
							(pReadErr, pReadBody) =>
							{
								const tmpBlob = (pReadBody && typeof(pReadBody) === 'object') ? pReadBody : {};
								const tmpSourceBlob = tmpBlob[pEntry.SourceConfig];
								if (pReadErr || tmpSourceBlob === undefined || tmpSourceBlob === null)
								{
									tmpWarnings.push(`Email rule config: source config EmailRule-${pEntry.Handler}/${pEntry.SourceConfig} (customer ${pEntry.SourceCustomerID}) not found — cloned rule ${tmpNewID} left pointing at the source value.`);
									return fNext();
								}
								// 3. Copy it to the cloned rule's own namespace value.
								const tmpNewValue = `IDEmailRule-${tmpNewID}`;
								const tmpWriteURI = `Configuration/Put/EmailRule-${encodeURIComponent(pEntry.Handler)}/${encodeURIComponent(tmpNewValue)}/Customer/${pEntry.DestCustomerID}`;
								pDestClient.postJSON(tmpWriteURI, tmpSourceBlob,
									(pWriteErr) =>
									{
										if (pWriteErr)
										{
											tmpWarnings.push(`Email rule config: failed to write EmailRule-${pEntry.Handler}/${tmpNewValue} for rule ${tmpNewID} (${(pWriteErr && pWriteErr.message) || pWriteErr}).`);
											return fNext();
										}
										// 4. Point the cloned rule at its own config value. Carry
										//    IDCustomer so the update isn't read as a customer change.
										pDestClient.updateEntity('EmailRule', { IDEmailRule: tmpNewID, IDCustomer: pEntry.DestCustomerID, Config: tmpNewValue },
											(pUpdErr) =>
											{
												if (pUpdErr)
												{
													tmpWarnings.push(`Email rule config: copied config but failed to update rule ${tmpNewID}.Config to ${tmpNewValue} (${(pUpdErr && pUpdErr.message) || pUpdErr}).`);
													return fNext();
												}
												tmpSynced++;
												this.log.info(`  Email rule ${tmpNewID}: cloned per-rule config → ${tmpNewValue}`,
													{ Action: 'ProjectCloning-EmailRuleConfigCloned', IDEmailRule: tmpNewID, Handler: pEntry.Handler });
												return fNext();
											});
									});
							});
					});
			},
			() =>
			{
				this.log.info(`  Email-rule config sync: ${tmpSynced}/${pMeta.length} per-rule config(s) cloned.`);
				return fCallback(null, { synced: tmpSynced, warnings: tmpWarnings });
			});
	}
}

module.exports = ProjectCloningAdapter;

module.exports.default_configuration = {
	Name: 'ProjectCloningAdapter',
	GUIDPrefix: DEFAULT_GUID_PREFIX,
};

module.exports.CLONED_ENTITIES = CLONED_ENTITIES;
module.exports.SAMPLE_PLAN_ENTITIES = SAMPLE_PLAN_ENTITIES;

